import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhoneCall, PhoneOff, PhoneMissed, Flame, RotateCcw, Ghost, CalendarClock,
  ChevronRight, ChevronLeft, ChevronDown, MapPin, Mail, Info, CheckCircle2, XCircle, Clock,
  Loader2, Sparkles, ArrowRight, Building2, Plus, Ban, Star, Search, Phone, RefreshCw,
  Trash2, ExternalLink, ListChecks, X, Trophy, TrendingUp, History, Euro, Send, MailCheck, Wand2, SkipForward, Paperclip, Users,
  Pencil, Save, User, Undo2, ArrowLeft, NotebookPen, FileSpreadsheet, AlertTriangle, CalendarCheck,
  Filter, Map, Target, Gauge
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { PageHeader } from "@/components/AppShell";
import { DialerButton } from "@/components/Dialer";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { AllContacts, ContactChips } from "@/components/ContactBlock";
import { DatesPicker } from "@/components/DatesPicker";
import { SavedViews } from "@/components/SavedViews";
import { contactPhones, contactName, frenchDate } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const REASON = {
  rebooking: { label: "Rebooking à chaud", icon: Flame, color: "#EE722E" },
  rappel: { label: "Rappel dû", icon: CalendarClock, color: "#C0392B" },
  relance_oui: { label: "Relance oui à l'oral", icon: Ghost, color: "#8B5CF6" },
  prospection: { label: "Nouveau prospect", icon: PhoneCall, color: "#1F9D55" },
};

// Tier de performance d'un spot (calculé côté backend à partir des dons recrutés)
const PERF_TIER = {
  top: { label: "Top perf", icon: Trophy, color: "#B45309", bg: "rgba(233,184,0,0.18)" },
  bon: { label: "Bon", icon: TrendingUp, color: "#0F5A2F", bg: "rgba(31,157,85,0.14)" },
  moyen: { label: "Moyen", icon: Star, color: "#5A5A5A", bg: "rgba(90,90,90,0.10)" },
};

const QUICK_RESULTS = [
  { v: "repondeur", l: "Répondeur", icon: PhoneOff },
  { v: "nrp", l: "NRP", icon: PhoneMissed },
  { v: "a_rappeler", l: "À rappeler", icon: Clock },
  { v: "refus_temporaire", l: "Refus temporaire", icon: XCircle },
  { v: "refus_definitif", l: "Refus définitif", icon: XCircle },
  { v: "oui_oral", l: "Oui à l'oral", icon: CheckCircle2 },
  { v: "date_posee", l: "Date posée", icon: CalendarClock },
];

const END_MAP = {
  __end_date__: "date_posee",
  __end_proposition__: "proposition",
  __end_rappel__: "a_rappeler",
  __end_refus__: "refus_temporaire",
  __end_nrp__: "nrp",
};

// --- Rappels : helpers de date + raccourcis rapides -------------------------
const _pad2 = (n) => String(n).padStart(2, "0");
const _isoDay = (d) => `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;

// Télécharge un export Excel authentifié (blob) et déclenche l'enregistrement.
async function downloadExport(path, fallbackName) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    const fname = m ? m[1] : fallbackName;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = fname; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Export Excel téléchargé");
  } catch {
    toast.error("Export impossible pour le moment");
  }
}
const CALLBACK_PRESETS = [
  { key: "1h", label: "Dans 1h", compute: () => { const d = new Date(); d.setHours(d.getHours() + 1, 0); return { d: _isoDay(d), t: `${_pad2(d.getHours())}:00` }; } },
  { key: "apresmidi", label: "Cet après-midi", compute: () => ({ d: _isoDay(new Date()), t: "14:00" }) },
  { key: "demain", label: "Demain 10h", compute: () => { const d = new Date(); d.setDate(d.getDate() + 1); return { d: _isoDay(d), t: "10:00" }; } },
  { key: "2jours", label: "Dans 2 jours", compute: () => { const d = new Date(); d.setDate(d.getDate() + 2); return { d: _isoDay(d), t: "10:00" }; } },
  { key: "semaine", label: "Dans 1 semaine", compute: () => { const d = new Date(); d.setDate(d.getDate() + 7); return { d: _isoDay(d), t: "10:00" }; } },
];

// ---------------------------------------------------------------------------
// [ANTI-TÉLESCOPAGE] Bouton d'appel « protégé » : avant de composer, on vérifie
// EN DIRECT si le magasin a déjà un passage programmé (booking futur). Si oui,
// on demande une confirmation explicite pour éviter d'appeler un magasin qu'un
// autre prospecteur travaille déjà. Réutilisé dans la file ET « À rappeler ».
// ---------------------------------------------------------------------------
function GuardedCallButton({ spotId, phone, contactName: cName, spotName, size = "sm", testId }) {
  const { startCall, status, config, meta } = useCall();
  const [checking, setChecking] = useState(false);
  const [guard, setGuard] = useState(null); // { date, association, status_label, booked_by }
  const busy = ["connecting", "ringing", "in-call"].includes(status);
  const isThis = busy && meta?.spotId === spotId;
  const xs = size === "xs";

  const doCall = () => startCall({ spotId, toNumber: phone, contactName: cName || spotName, spotName });

  const onClick = async (e) => {
    e.stopPropagation();
    if (busy || !config.ready || !phone) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/prospection/booking-check/${spotId}`);
      if (data?.has_future_booking) { setGuard(data.next_booking || {}); return; }
      doCall();
    } catch { doCall(); }   // si le contrôle échoue, on ne bloque pas l'appel
    finally { setChecking(false); }
  };

  return (
    <>
      <span role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter") onClick(e); }}
        data-testid={testId}
        title={config.ready ? "Appeler dans la plateforme" : "Téléphonie en attente d'activation"}
        className={`inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.22)] ${(busy || !config.ready) ? "opacity-60" : "cursor-pointer"} ${xs ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1.5 text-[11px]"}`}>
        {(isThis || checking) ? <Loader2 className={xs ? "h-2.5 w-2.5 animate-spin" : "h-3 w-3 animate-spin"} /> : <PhoneCall className={xs ? "h-2.5 w-2.5" : "h-3 w-3"} />}
        {phone}
      </span>
      <AlertDialog open={!!guard} onOpenChange={(o) => { if (!o) setGuard(null); }}>
        <AlertDialogContent data-testid="call-telescope-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#8F2A20]">
              <AlertTriangle className="h-5 w-5" /> Magasin déjà programmé
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p><span className="font-semibold">{spotName}</span> a déjà un passage prévu :</p>
                <div className="rounded-lg border border-[rgba(192,57,43,0.25)] bg-[rgba(192,57,43,0.06)] p-2.5 text-[#8F2A20]">
                  <div className="font-bold">
                    {guard?.date ? frenchDate(guard.date) : "date à venir"}
                    {guard?.association ? ` · ${guard.association}` : ""}
                    {guard?.status_label ? ` · ${guard.status_label}` : ""}
                  </div>
                  {guard?.booked_by && <div className="mt-0.5 text-[11px]">Posé par : {guard.booked_by}</div>}
                </div>
                <p className="text-muted-ink">Pour éviter le télescopage, mieux vaut ne pas rappeler un magasin qu'un autre prospecteur travaille déjà.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="call-telescope-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="call-telescope-confirm"
              onClick={(e) => { e.preventDefault(); setGuard(null); doCall(); }}
              className="bg-[#8F2A20] text-white hover:bg-[#75211a]">
              Appeler quand même
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// [ALERTE ROTATION] Encart NON bloquant : quand le CDP choisit des dates de
// proposition/confirmation, on vérifie qu'elles respectent le délai de rotation
// du spot. Si une date est trop proche du dernier passage, on l'affiche avec la
// prochaine date conseillée. Le CDP reste libre d'envoyer (alerte informative).
// ---------------------------------------------------------------------------
function RotationDatesAlert({ spotId, associationId, isoDates }) {
  const [conflicts, setConflicts] = useState([]);
  const key = (isoDates || []).join(",");
  useEffect(() => {
    if (!spotId || !associationId || !isoDates || isoDates.length === 0) { setConflicts([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/prospection/check-dates", {
          spot_id: spotId, association_id: associationId, dates: isoDates,
        });
        if (!cancelled) setConflicts((data.results || []).filter((r) => !r.allowed));
      } catch { if (!cancelled) setConflicts([]); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [spotId, associationId, key]); // eslint-disable-line
  if (conflicts.length === 0) return null;
  return (
    <div className="mt-2 rounded-xl border border-[rgba(233,184,0,0.5)] bg-[rgba(233,184,0,0.10)] p-3" data-testid="rotation-alert">
      <div className="flex items-center gap-1.5 text-[12px] font-bold text-[#8A6D00]">
        <AlertTriangle className="h-4 w-4" />
        {conflicts.length} date{conflicts.length > 1 ? "s" : ""} hors délai de rotation
      </div>
      <ul className="mt-1.5 space-y-1">
        {conflicts.map((r) => (
          <li key={r.date} className="text-[11px] text-[#6B5500]" data-testid={`rotation-conflict-${r.date}`}>
            <span className="font-semibold">{frenchDate(r.date)}</span> — disponible plutôt à partir du{" "}
            <span className="font-semibold">{r.next_available ? frenchDate(r.next_available) : "…"}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-[10px] text-[#6B5500]">Ces dates sont proches du dernier passage (rotation). Vous pouvez continuer si c'est volontaire.</p>
    </div>
  );
}


export default function Prospection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [queue, setQueue] = useState(null);
  const [callbacks, setCallbacks] = useState(null);
  const [refus, setRefus] = useState(null);
  // Onglet actif : pilotable depuis l'URL (?tab=google) pour un accès direct
  // depuis la Banque de spots (« Enrichir via Google »).
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return ["appels", "rappels", "google", "couverture", "refus"].includes(t) ? t : "appels";
  });
  const [cbSession, setCbSession] = useState(null);   // { list:[...], index:n } — parcours à la chaîne des rappels
  const [scripts, setScripts] = useState([]);
  const [assocs, setAssocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qSearch, setQSearch] = useState("");
  const [enseigneFilter, setEnseigneFilter] = useState(null);
  const [deptFilter, setDeptFilter] = useState(null);

  const loadCallbacks = useCallback(async () => {
    try {
      const { data } = await api.get("/prospection/callbacks");
      setCallbacks(data);
    } catch { /* silencieux : la file principale reste utilisable */ }
  }, []);

  const loadRefus = useCallback(async () => {
    try {
      const { data } = await api.get("/prospection/refus");
      setRefus(data);
    } catch { /* silencieux */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s, a, c] = await Promise.all([
        api.get("/prospection/queue"), api.get("/prospection/scripts"),
        api.get("/associations"), api.get("/prospection/callbacks"),
      ]);
      setQueue(q.data); setScripts(s.data); setAssocs(a.data); setCallbacks(c.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefus(); }, [loadRefus]);

  const allItems = queue?.items || [];
  // Enseignes présentes dans la file -> puces de filtre rapide (on en montre beaucoup
  // pour couvrir Franprix, Decathlon, etc. ; la recherche libre couvre le reste).
  const enseignes = useMemo(() => {
    const c = {};
    allItems.forEach((it) => { const e = (it.enseigne || "?").trim(); if (e) c[e] = (c[e] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 24).map(([e, n]) => ({ e, n }));
  }, [allItems]);
  // Départements présents dans la file -> puces pour affiner par proximité géographique
  const depts = useMemo(() => {
    const c = {};
    allItems.forEach((it) => { const d = String(it.dept || "").trim(); if (d) c[d] = (c[d] || 0) + 1; });
    return Object.entries(c)
      .sort((a, b) => b[1] - a[1])            // on garde les départements les plus fournis
      .slice(0, 20)                            // limite raisonnable d'affichage
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }))
      .map(([d, n]) => ({ d, n }));
  }, [allItems]);
  const filteredItems = useMemo(() => {
    const q = qSearch.trim().toLowerCase();
    return allItems.filter((it) => {
      if (enseigneFilter && (it.enseigne || "") !== enseigneFilter) return false;
      if (deptFilter && String(it.dept || "") !== String(deptFilter)) return false;
      if (q) {
        const hay = `${it.normalized_name || ""} ${it.ville || ""} ${it.dept || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allItems, qSearch, enseigneFilter, deptFilter]);

  // Statistiques de la session d'appels (gamification / récap de fin de file)
  const [sessionStats, setSessionStats] = useState({ traites: 0, accords: 0, emails: 0 });
  const bumpStat = (key, n = 1) => setSessionStats((s) => ({ ...s, [key]: (s[key] || 0) + n }));

  // ----- Navigation unifiée (file d'appels OU liste de rappels) -----------------
  // Liste actuellement parcourue + index courant. En mode rappel on parcourt la
  // liste des rappels (cbSession.list) ; sinon la file d'appels filtrée.
  const navList = cbSession ? cbSession.list : (filteredItems.length ? filteredItems : allItems);
  const navIndex = cbSession
    ? cbSession.index
    : navList.findIndex((i) => i.spot_id === selected?.spot_id);

  const selectAt = useCallback((idx) => {
    if (idx < 0 || idx >= navList.length) return;
    setSelected(navList[idx]);
    if (cbSession) setCbSession((s) => (s ? { ...s, index: idx } : s));
  }, [navList, cbSession]);

  // Avance INSTANTANÉE au suivant (aucun rechargement bloquant -> réactif au clic/clavier).
  const goNextInstant = useCallback(() => {
    if (navIndex < 0) return;
    if (navIndex + 1 < navList.length) { selectAt(navIndex + 1); return; }
    if (cbSession) {
      toast.success("Tous les rappels de la liste ont été traités.");
      setCbSession(null); setSelected(null); setTab("rappels"); loadCallbacks();
    } else {
      setSelected(null); toast.success("File d'appels terminée — bravo !");
    }
  }, [navIndex, navList, cbSession, selectAt, loadCallbacks]);

  // Recul INSTANTANÉ au précédent.
  const goPrevInstant = useCallback(() => {
    if (navIndex > 0) selectAt(navIndex - 1);
  }, [navIndex, selectAt]);

  // Après un RÉSULTAT d'appel : on avance tout de suite, puis on rafraîchit la file
  // et les rappels EN ARRIÈRE-PLAN (le spot traité en sort au prochain rendu).
  const advanceAfterResult = useCallback(() => {
    goNextInstant();
    api.get("/prospection/queue").then(({ data }) => setQueue(data)).catch(() => {});
    loadCallbacks();
  }, [goNextInstant, loadCallbacks]);

  // Ouvre l'assistant d'appel sur un spot venu de la timeline « À rappeler ».
  // Mémorise la LISTE en cours + l'index -> permet d'enchaîner les rappels.
  const openFromCallback = (item, list, index) => {
    setCbSession({ list: Array.isArray(list) && list.length ? list : [item], index: index ?? 0 });
    setSelected(item);
    setTab("appels");
  };

  // Sortie du mode rappel : retour à la liste À rappeler (rafraîchie).
  const exitCallbacks = () => {
    setCbSession(null); setSelected(null); setTab("rappels"); loadCallbacks();
  };

  // Bascule du mode rappel vers la file d'appels normale (sans changer d'onglet).
  const backToQueue = () => { setCbSession(null); setSelected(null); };

  // Navigation clavier ← / → dans l'assistant (ignorée pendant une saisie de texte).
  useEffect(() => {
    if (tab !== "appels" || !selected) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = (t && t.tagName ? t.tagName : "").toLowerCase();
      if ((t && t.isContentEditable) || tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "ArrowRight") { e.preventDefault(); goNextInstant(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrevInstant(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, selected, goNextInstant, goPrevInstant]);

  // Live Edit : propage la mise à jour des coordonnées au spot sélectionné ET à la file
  // (sans recharger la file pour ne pas perdre le contexte d'appel en cours).
  const handleContactUpdated = (spotId, newContact) => {
    setSelected((s) => (s && s.spot_id === spotId ? { ...s, contact: newContact } : s));
    setQueue((q) => {
      if (!q) return q;
      const items = (q.items || []).map((it) =>
        it.spot_id === spotId ? { ...it, contact: newContact } : it);
      return { ...q, items };
    });
  };

  return (
    <>
      <PageHeader title="Prospection & assistant d'appel"
        subtitle={`Votre file d'appels du jour${queue ? ` — ${queue.total} spots à traiter` : ""}. Le bon script s'affiche au bon moment.`}
        action={<DialerButton onSpotCreated={load} triggerLabel="Composer un numéro" data-testid="prospection-dialer-btn" />} />
      <div className="mx-auto max-w-[1280px] px-4 py-4 sm:px-6 lg:px-8">
        {user?.recovery_mode && (
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-[rgba(155,34,38,0.25)] bg-[rgba(155,34,38,0.06)] p-3" data-testid="recovery-banner">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-[#9B2226]" />
            <div className="text-[12px] leading-snug">
              <p className="font-bold text-[#7A1E22]">Mode reconquête · développement partenarial</p>
              <p className="text-muted-ink">Votre portefeuille d'emplacements à reconquérir (tous secteurs) s'affiche directement dans l'assistant d'appel, avec tout l'historique. Filtrez par département pour cibler votre zone.</p>
            </div>
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4" data-testid="prospection-tabs">
            <TabsTrigger value="appels" data-testid="tab-appels"><PhoneCall className="mr-1.5 h-4 w-4" />Assistant d'appel</TabsTrigger>
            <TabsTrigger value="rappels" data-testid="tab-rappels">
              <CalendarClock className="mr-1.5 h-4 w-4" />À rappeler
              {callbacks?.counts?.actionable > 0 && (
                <span className={`ml-1.5 grid min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold ${callbacks.counts.en_retard > 0 ? "bg-[#C0392B] text-white" : "bg-[var(--shariti-orange)] text-white"}`} data-testid="rappels-badge">
                  {callbacks.counts.actionable}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="google" data-testid="tab-google"><Sparkles className="mr-1.5 h-4 w-4" />Suggestions Google</TabsTrigger>
            <TabsTrigger value="couverture" data-testid="tab-couverture"><Gauge className="mr-1.5 h-4 w-4" />Couverture</TabsTrigger>
            <TabsTrigger value="refus" data-testid="tab-refus">
              <Ban className="mr-1.5 h-4 w-4" />Refus
              {refus?.counts?.total > 0 && (
                <span className="ml-1.5 grid min-w-[18px] place-items-center rounded-full bg-[#9B2226] px-1 text-[10px] font-bold text-white" data-testid="refus-badge">
                  {refus.counts.total}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="appels">
            <div className="grid gap-4 @4xl/page:grid-cols-[360px_1fr]">
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3">
          <div className="mb-2 flex items-center justify-between px-1">
            <h3 className="text-sm font-bold">{cbSession ? "Rappels à traiter" : "File d'appels"}</h3>
            <div className="flex items-center gap-1">
              {cbSession ? (
                <Button variant="ghost" size="sm" onClick={backToQueue} className="h-7 text-xs" data-testid="switch-to-queue" title="Revenir à la file d'appels">
                  <PhoneCall className="mr-1 h-3.5 w-3.5" />File d'appels
                </Button>
              ) : (
                <>
                  <SavedViews page="prospection"
                    current={{ qSearch, enseigneFilter, deptFilter }}
                    onApply={(p) => { setQSearch(p.qSearch || ""); setEnseigneFilter(p.enseigneFilter ?? null); setDeptFilter(p.deptFilter ?? null); }}
                    describe={(p) => [p.deptFilter && `Dépt ${p.deptFilter}`, p.enseigneFilter, p.qSearch && `« ${p.qSearch} »`].filter(Boolean).join(" · ") || "Tous"}
                    label="Favoris" buttonVariant="ghost" buttonClassName="h-7 text-xs" />
                  <Button variant="ghost" size="sm" onClick={load} className="h-7 text-xs" data-testid="queue-refresh"><RotateCcw className="mr-1 h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>
          </div>
          {/* Recherche + filtre enseigne (file d'appels uniquement) */}
          {!cbSession && (
          <div className="mb-2 space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
              <Input value={qSearch} onChange={(e) => setQSearch(e.target.value)} placeholder="Rechercher (enseigne, ville…)"
                className="h-9 pl-8 text-xs" data-testid="queue-search" />
            </div>
            {enseignes.length > 1 && (
              <div className="flex flex-wrap gap-1" data-testid="queue-enseigne-chips">
                {enseignes.map(({ e, n }) => {
                  const active = enseigneFilter === e;
                  return (
                    <button key={e} onClick={() => setEnseigneFilter(active ? null : e)} data-testid={`queue-enseigne-${e}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${active ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                      {e} <span className="opacity-60">{n}</span>
                    </button>
                  );
                })}
                {enseigneFilter && (
                  <button onClick={() => setEnseigneFilter(null)} data-testid="queue-enseigne-clear"
                    className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
            {depts.length > 1 && (
              <div className="flex flex-wrap items-center gap-1" data-testid="queue-dept-chips">
                <span className="mr-0.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-muted-ink">
                  <MapPin className="h-3 w-3" />Dépt
                </span>
                {depts.map(({ d, n }) => {
                  const active = deptFilter === d;
                  return (
                    <button key={d} onClick={() => setDeptFilter(active ? null : d)} data-testid={`queue-dept-${d}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${active ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                      {d} <span className="opacity-60">{n}</span>
                    </button>
                  );
                })}
                {deptFilter && (
                  <button onClick={() => setDeptFilter(null)} data-testid="queue-dept-clear"
                    className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
          )}
          {cbSession ? (
            <div className="max-h-[74vh] space-y-1.5 overflow-y-auto no-scrollbar" data-testid="callback-list">
              <div className="px-1 pb-1 text-[10px] font-semibold text-muted-ink" data-testid="callback-count">
                {cbSession.list.length} rappel{cbSession.list.length > 1 ? "s" : ""} · en cours n°{cbSession.index + 1}
              </div>
              {cbSession.list.map((it, i) => {
                const active = i === cbSession.index;
                const overdue = it.bucket === "en_retard" || it.overdue_now;
                return (
                  <button key={`${it.spot_id}-${i}`} onClick={() => selectAt(i)} data-testid={`callback-item-${it.spot_id}`}
                    className={`w-full rounded-xl border p-2.5 text-left transition-colors ${active ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-extrabold leading-tight line-clamp-2">{it.normalized_name}</span>
                      <span className="mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ background: overdue ? "rgba(192,57,43,0.14)" : "rgba(238,114,46,0.14)", color: overdue ? "#8F2A20" : "#B5470F" }}>
                        {overdue ? "En retard" : "Rappel"}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-ink">
                      <MapPin className="h-3 w-3" />{it.ville} {it.dept ? `(${it.dept})` : ""}
                    </div>
                    <div className="mt-1 text-[10px] font-bold" style={{ color: overdue ? "#C0392B" : "#B5470F" }}>
                      {frenchDate(it.next_callback_date)}{it.heure_rappel ? ` · ${it.heure_rappel}` : ""}
                    </div>
                    {it.note && (
                      <div className="mt-1.5 flex items-start gap-1 rounded-lg bg-[rgba(47,111,237,0.06)] px-2 py-1 text-[10px] text-[#1F4E9B]">
                        <NotebookPen className="mt-0.5 h-3 w-3 shrink-0" /><span className="line-clamp-2">{it.note}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : loading ? (
            <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-[var(--surface-2)]" />)}</div>
          ) : allItems.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="File vide" message="Aucun appel en attente pour aujourd'hui." />
          ) : filteredItems.length === 0 ? (
            <EmptyState icon={Search} title="Aucun résultat" message="Aucun spot ne correspond à votre recherche ou filtre." />
          ) : (
            <div className="max-h-[70vh] space-y-1.5 overflow-y-auto no-scrollbar" data-testid="queue-list">
              <div className="px-1 pb-1 text-[10px] font-semibold text-muted-ink" data-testid="queue-filtered-count">
                {filteredItems.length} spot{filteredItems.length > 1 ? "s" : ""}{(qSearch || enseigneFilter || deptFilter) ? ` (sur ${allItems.length})` : ""}
              </div>
              {queue?.hidden_future_booked > 0 && (
                <div className="mx-1 mb-1.5 flex items-center gap-1.5 rounded-lg bg-[rgba(47,111,237,0.07)] px-2 py-1 text-[10px] font-semibold text-[#1F4E9B]" data-testid="queue-hidden-booked">
                  <CalendarCheck className="h-3 w-3 shrink-0" />
                  {queue.hidden_future_booked} magasin{queue.hidden_future_booked > 1 ? "s" : ""} déjà programmé{queue.hidden_future_booked > 1 ? "s" : ""} masqué{queue.hidden_future_booked > 1 ? "s" : ""} — zéro télescopage
                </div>
              )}
              {filteredItems.map((it) => {
                const meta = REASON[it.reason] || REASON.prospection;
                const RIcon = meta.icon;
                const active = selected?.spot_id === it.spot_id;
                const tier = it.perf?.tier ? PERF_TIER[it.perf.tier] : null;
                const TIcon = tier?.icon;
                return (
                  <button key={it.spot_id} onClick={() => { setCbSession(null); setSelected(it); }} data-testid={`queue-item-${it.spot_id}`}
                    className={`w-full rounded-xl border p-2.5 text-left transition-colors ${active ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-extrabold leading-tight line-clamp-2">{it.normalized_name}</span>
                      <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ background: `${meta.color}18`, color: meta.color }}>
                        <RIcon className="h-2.5 w-2.5" />{meta.label}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-ink">
                      <MapPin className="h-3 w-3" />{it.ville} {it.dept ? `(${it.dept})` : ""}
                      {tier && (
                        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold" style={{ background: tier.bg, color: tier.color }}
                          title={`${it.perf.valid} don(s) · ${it.perf.avg_per_day}/jour`} data-testid={`queue-perf-${it.spot_id}`}>
                          <TIcon className="h-2.5 w-2.5" />{tier.label} · {it.perf.avg_per_day}/j
                        </span>
                      )}
                      {contactPhones(it.contact)[0] && (
                        <span className="ml-auto">
                          <GuardedCallButton spotId={it.spot_id} phone={contactPhones(it.contact)[0]}
                            contactName={contactName(it.contact)} spotName={it.normalized_name}
                            size="xs" testId={`queue-tel-${it.spot_id}`} />
                        </span>
                      )}
                    </div>
                    {it.reason === "rappel" && it.next_callback_date && (
                      <div className="mt-1 text-[10px] font-bold" style={{ color: "#C0392B" }} data-testid={`queue-rappel-date-${it.spot_id}`}>
                        {queue?.date && it.next_callback_date < queue.date ? "En retard" : "À rappeler"} · {frenchDate(it.next_callback_date)}{it.heure_rappel ? ` · ${it.heure_rappel}` : ""}
                      </div>
                    )}
                    {it.note && (
                      <div className="mt-1.5 flex items-start gap-1 rounded-lg bg-[rgba(47,111,237,0.06)] px-2 py-1 text-[10px] text-[#1F4E9B]" data-testid={`queue-note-${it.spot_id}`}>
                        <NotebookPen className="mt-0.5 h-3 w-3 shrink-0" />
                        <span className="line-clamp-2">{it.note}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          {!selected ? (
            sessionStats.traites > 0 ? (
              <SessionRecap stats={sessionStats} onReset={() => { setSessionStats({ traites: 0, accords: 0, emails: 0 }); load(); }} />
            ) : (
              <EmptyState icon={PhoneCall} title="Sélectionnez un appel"
                message="Choisissez un spot dans votre file pour lancer l'assistant d'appel avec le script adapté." />
            )
          ) : (
            <CallAssistant item={selected} scripts={scripts} assocs={assocs} user={user}
              callbackMode={!!cbSession}
              queuePosition={cbSession ? { index: cbSession.index, total: cbSession.list.length } : {
                index: (filteredItems.length ? filteredItems : allItems).findIndex((i) => i.spot_id === selected.spot_id),
                total: (filteredItems.length ? filteredItems : allItems).length,
              }}
              onStat={bumpStat}
              onNext={advanceAfterResult}
              onSkip={goNextInstant}
              onPrev={goPrevInstant}
              onBackToList={cbSession ? exitCallbacks : null}
              onContactUpdated={handleContactUpdated}
              onClose={cbSession ? exitCallbacks : () => { setSelected(null); load(); }}
              navigate={navigate} />
          )}
        </div>
          </div>
          </TabsContent>
          <TabsContent value="rappels">
            <CallbacksView data={callbacks} loading={loading} onOpen={openFromCallback} onRefresh={loadCallbacks} />
          </TabsContent>
          <TabsContent value="google">
            <GoogleSuggestions user={user} navigate={navigate}
              initialQ={searchParams.get("q") || ""}
              autoDiscover={searchParams.get("discover") === "1"} />
          </TabsContent>
          <TabsContent value="couverture">
            <CoverageView navigate={navigate} />
          </TabsContent>
          <TabsContent value="refus">
            <RefusView data={refus} navigate={navigate} onRefresh={() => { loadRefus(); load(); }} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Vue dédiée « À rappeler » : timeline horodatée des rappels programmés, avec
// filtres rapides (en retard / aujourd'hui / à venir) et alertes visuelles.
// ---------------------------------------------------------------------------
const CB_REASON_STYLE = {
  "Refus temporaire": { bg: "rgba(233,184,0,0.16)", color: "#5A4200" },
  "NRP / Répondeur": { bg: "rgba(90,90,90,0.10)", color: "#4A4A4A" },
  "À rappeler": { bg: "rgba(238,114,46,0.12)", color: "#B5470F" },
};

function CallbackRow({ it, list, index, onOpen }) {
  const phone = contactPhones(it.contact)[0];
  const overdue = it.bucket === "en_retard" || it.overdue_now;
  const soon = it.bucket === "aujourdhui" && !it.overdue_now;
  const pill = overdue
    ? {
        bg: "rgba(192,57,43,0.12)", color: "#8F2A20",
        label: it.bucket === "en_retard"
          ? `En retard · ${frenchDate(it.next_callback_date)}${it.heure_rappel ? ` · ${it.heure_rappel}` : ""}`
          : `${it.heure_rappel || "Aujourd'hui"} · dépassé`,
      }
    : soon
    ? { bg: "rgba(238,114,46,0.12)", color: "#B5470F", label: it.heure_rappel ? `Aujourd'hui · ${it.heure_rappel}` : "Aujourd'hui" }
    : { bg: "rgba(47,111,237,0.10)", color: "#1F4FB0", label: `${frenchDate(it.next_callback_date)}${it.heure_rappel ? ` · ${it.heure_rappel}` : ""}` };
  const rStyle = CB_REASON_STYLE[it.reason] || CB_REASON_STYLE["À rappeler"];

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 transition-colors ${overdue ? "border-[rgba(192,57,43,0.35)] bg-[rgba(192,57,43,0.04)]" : "border-[var(--border-soft)] bg-white hover:border-[rgba(238,114,46,0.28)]"}`}
      data-testid={`callback-row-${it.spot_id}`}>
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
        style={{ background: pill.bg, color: pill.color }} data-testid={`callback-time-${it.spot_id}`}>
        <Clock className="h-3.5 w-3.5" />{pill.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="break-words text-sm font-extrabold leading-tight">{it.normalized_name}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-ink">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{it.ville}{it.dept ? ` (${it.dept})` : ""}</span>
          <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-bold" style={{ background: rStyle.bg, color: rStyle.color }}>{it.reason}</span>
          {it.heure_rappel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 font-bold text-[#B5470F]" data-testid={`callback-hour-${it.spot_id}`}>
              <Clock className="h-3 w-3" />{it.heure_rappel}
            </span>
          )}
          {it.nrp_attempts > 0 && <span className="text-muted-ink">{it.nrp_attempts} tentative{it.nrp_attempts > 1 ? "s" : ""}</span>}
        </div>
        {it.note && (
          <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-muted-ink" data-testid={`callback-note-${it.spot_id}`}>
            <NotebookPen className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
            <span className="whitespace-pre-line break-words"><span className="font-bold">Note : </span>{it.note}</span>
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {phone && (
          <GuardedCallButton spotId={it.spot_id} phone={phone}
            contactName={contactName(it.contact)} spotName={it.normalized_name}
            size="sm" testId={`callback-call-${it.spot_id}`} />
        )}
        <Button size="sm" onClick={() => onOpen(it, list, index)} data-testid={`callback-open-${it.spot_id}`}
          className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
          Ouvrir <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

const CB_FILTERS = [
  { key: "a_traiter", label: "À traiter" },
  { key: "en_retard", label: "En retard" },
  { key: "aujourdhui", label: "Aujourd'hui" },
  { key: "a_venir", label: "À venir" },
];

function CallbacksView({ data, loading, onOpen, onRefresh }) {
  const [filter, setFilter] = useState("a_traiter");
  const counts = data?.counts || {};
  const lists = useMemo(() => {
    const er = data?.en_retard || [];
    const auj = data?.aujourdhui || [];
    const av = data?.a_venir || [];
    return {
      a_traiter: [...er, ...auj],
      en_retard: er,
      aujourdhui: auj,
      a_venir: av,
    };
  }, [data]);
  const rows = lists[filter] || [];
  const overdueCount = (data?.en_retard?.length || 0) + (data?.aujourdhui || []).filter((i) => i.overdue_now).length;

  const chipCount = (key) => key === "a_traiter" ? (counts.actionable || 0)
    : key === "en_retard" ? (counts.en_retard || 0)
    : key === "aujourdhui" ? (counts.aujourdhui || 0)
    : (counts.a_venir || 0);

  return (
    <div className="mx-auto max-w-[900px]" data-testid="callbacks-view">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-bold"><CalendarClock className="h-4 w-4 text-[var(--shariti-orange)]" />Rappels programmés</h3>
            <p className="mt-0.5 text-[11px] text-muted-ink">Qui rappeler, et à quelle heure — votre journée en un coup d'œil.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-8 text-xs" data-testid="callbacks-refresh">
            <RotateCcw className="mr-1 h-3.5 w-3.5" />Actualiser
          </Button>
        </div>

        {/* Alerte : rappels en retard */}
        {overdueCount > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-[rgba(192,57,43,0.3)] bg-[rgba(192,57,43,0.06)] px-3 py-2 text-[12px] font-semibold text-[#8F2A20]" data-testid="callbacks-overdue-alert">
            <Clock className="h-4 w-4" />
            {overdueCount} rappel{overdueCount > 1 ? "s" : ""} en retard — à traiter en priorité.
          </div>
        )}

        {/* Anti-télescopage : magasins déjà programmés masqués de la liste */}
        {data?.hidden_future_booked > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-[rgba(47,111,237,0.25)] bg-[rgba(47,111,237,0.06)] px-3 py-2 text-[12px] font-semibold text-[#1F4E9B]" data-testid="callbacks-hidden-booked">
            <CalendarCheck className="h-4 w-4" />
            {data.hidden_future_booked} magasin{data.hidden_future_booked > 1 ? "s" : ""} déjà programmé{data.hidden_future_booked > 1 ? "s" : ""} masqué{data.hidden_future_booked > 1 ? "s" : ""} — zéro télescopage.
          </div>
        )}

        {/* Filtres rapides */}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="callbacks-filters">
          {CB_FILTERS.map((f) => {
            const active = filter === f.key;
            const n = chipCount(f.key);
            const urgent = f.key === "en_retard" && n > 0;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`callbacks-filter-${f.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-transparent bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                {f.label}
                <span className={`grid min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold ${active ? "bg-white/25 text-white" : urgent ? "bg-[#C0392B] text-white" : "bg-[var(--surface-2)] text-muted-ink"}`}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Exports Excel « prêts terrain » (1 clic) */}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--border-soft)] pt-3" data-testid="callbacks-exports">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Exports Excel
          </span>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 border-[var(--border-soft)] bg-white text-xs"
            onClick={() => downloadExport("/prospection/exports/retard", "relances_en_retard.xlsx")}
            data-testid="export-retard-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 text-[#C0392B]" /> Relances en retard
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 border-[var(--border-soft)] bg-white text-xs"
            onClick={() => downloadExport("/prospection/exports/a-venir", "relances_a_venir.xlsx")}
            data-testid="export-a-venir-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 text-[#0F5A2F]" /> Relances à venir
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="mt-3 space-y-2">
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-[var(--surface-2)]" />)
        ) : rows.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="Aucun rappel"
            message={filter === "en_retard" ? "Aucun rappel en retard — vous êtes à jour !" : "Aucun rappel programmé pour ce filtre."} />
        ) : (
          rows.map((it, idx) => <CallbackRow key={`${it.spot_id}-${it.bucket}`} it={it} list={rows} index={idx} onOpen={onOpen} />)
        )}
      </div>
    </div>
  );
}


function CallButton({ item }) {
  const { startCall, status, config, meta } = useCall();
  const phones = contactPhones(item.contact);
  const phone = phones[0];
  const busy = ["connecting", "ringing", "in-call"].includes(status);
  const isThisCall = busy && meta?.spotId === item.spot_id;

  if (!phone) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button
        onClick={() => startCall({
          spotId: item.spot_id, toNumber: phone,
          contactName: contactName(item.contact) || item.normalized_name,
          spotName: item.normalized_name,
        })}
        disabled={busy || !config.ready}
        data-testid="prospection-call-btn"
        className="bg-[#1F9D55] text-white hover:bg-[#188045] disabled:opacity-60"
      >
        {isThisCall ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Phone className="mr-1.5 h-4 w-4" />}
        {isThisCall ? "Appel en cours…" : `Appeler ${phone}`}
      </Button>
      {!config.ready && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(252,219,83,0.35)] px-2.5 py-1 text-[11px] font-semibold text-[#5A4200]" data-testid="call-pending-hint">
          <Info className="h-3 w-3" />Téléphonie en attente d'activation
        </span>
      )}
    </div>
  );
}

// Libellés FR des actions du journal d'activité (traçabilité).
const ACTIVITY_ACTION_LABELS = {
  update_spot_contact: "Coordonnées modifiées",
  update_spot: "Fiche modifiée",
  call_result: "Résultat d'appel",
  book_confirmed_from_call: "Dates posées",
  create_spot: "Création du spot",
  prospect_add_to_base: "Ajout à la base",
  assign_booking: "Réservation",
  force_rebooking: "Rebooking forcé",
};

function activityTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return `${frenchDate(ts)} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return frenchDate(ts); }
}

/** Drawer latéral : historique complet des modifications d'un spot (traçabilité). */
function ContactHistoryDrawer({ open, onOpenChange, logs, loading, spotName }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="contact-history-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-4 w-4 text-[var(--shariti-orange)]" /> Historique des modifications
          </SheetTitle>
          <SheetDescription className="break-words">{spotName}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2.5">
          {loading ? (
            [0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-[var(--surface-2)]" />)
          ) : logs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--border-soft)] px-3 py-6 text-center text-sm text-muted-ink" data-testid="contact-history-empty">
              Aucune modification enregistrée pour l'instant.
            </div>
          ) : (
            logs.map((l) => {
              const isContact = l.action === "update_spot_contact";
              return (
                <div key={l.id} data-testid={`history-entry-${l.id}`}
                  className={`rounded-xl border p-3 ${isContact ? "border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.05)]" : "border-[var(--border-soft)] bg-white"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${isContact ? "bg-[rgba(238,114,46,0.15)] text-[var(--shariti-orange)]" : "bg-[var(--surface-2)] text-muted-ink"}`}>
                      {isContact ? <Pencil className="h-2.5 w-2.5" /> : <Info className="h-2.5 w-2.5" />}
                      {ACTIVITY_ACTION_LABELS[l.action] || l.action}
                    </span>
                    <span className="shrink-0 text-[10px] font-medium text-muted-ink">{activityTime(l.timestamp)}</span>
                  </div>
                  {l.details && <p className="mt-1.5 whitespace-pre-line break-words text-[12px] leading-relaxed text-ink">{l.details}</p>}
                  {l.meta?.next_callback_date && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[rgba(47,111,237,0.1)] px-2.5 py-1 text-[11px] font-bold text-[#1F4FB0]" data-testid={`history-callback-${l.id}`}>
                      <CalendarClock className="h-3.5 w-3.5" />
                      Rappel : {frenchDate(l.meta.next_callback_date)}{l.meta.heure_rappel ? ` · ${l.meta.heure_rappel}` : ""}
                    </div>
                  )}
                  {l.meta?.notes && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1.5 text-[11px] leading-relaxed text-ink" data-testid={`history-note-${l.id}`}>
                      <NotebookPen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink" />
                      <span className="whitespace-pre-line break-words"><span className="font-bold text-muted-ink">Note : </span>{l.meta.notes}</span>
                    </div>
                  )}
                  {l.user_name && (
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-muted-ink">
                      <User className="h-3 w-3" /> {l.user_name}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const CIVILITE_OPTIONS = [
  { v: "none", l: "—" },
  { v: "M.", l: "M." },
  { v: "Mme", l: "Mme" },
];

/**
 * Édition « à chaud » des coordonnées du contact principal pendant l'appel.
 * Mode lecture (contacts affichés) -> bouton « Modifier » -> formulaire -> « Enregistrer ».
 * Historique des modifications consultable (aperçu + drawer complet).
 */
function LiveContactEditor({ item, user, onContactUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ civilite: "", prenom: "", nom: "", fonction: "", telephone: "", email: "" });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const resetForm = useCallback(() => {
    const c = item.contact || {};
    setForm({
      civilite: c.civilite || "", prenom: c.prenom || "", nom: c.nom || "",
      fonction: c.fonction || "", telephone: c.telephone || "", email: c.email || "",
    });
  }, [item.contact]);

  useEffect(() => { resetForm(); setEditing(false); }, [item.spot_id]); // eslint-disable-line

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/spots/${item.spot_id}/activity`, { params: { limit: 50 } });
      setLogs(data.logs || []);
    } catch { setLogs([]); }
    finally { setLogsLoading(false); }
  }, [item.spot_id]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const contactLogs = logs.filter((l) => l.action === "update_spot_contact");
  const lastContactLog = contactLogs[0];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.email && !form.email.includes("@")) { toast.error("E-mail invalide (le « @ » est manquant)."); return; }
    setSaving(true);
    try {
      const newContact = {
        ...(item.contact || {}),
        civilite: form.civilite.trim(), prenom: form.prenom.trim(), nom: form.nom.trim(),
        fonction: form.fonction.trim(), telephone: form.telephone.trim(), email: form.email.trim(),
      };
      const { data } = await api.put(`/spots/${item.spot_id}`, {
        contact_principal: newContact, edit_source: "prospection",
      });
      if (data.contact_changes > 0) {
        toast.success("Coordonnées mises à jour", { description: "La modification est tracée dans l'historique." });
        onContactUpdated && onContactUpdated(item.spot_id, newContact);
        loadLogs();
      } else {
        toast.info("Aucune modification à enregistrer.");
      }
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour des coordonnées.");
    } finally { setSaving(false); }
  };

  const secondaires = item.contacts_secondaires || [];

  return (
    <div className="space-y-2.5" data-testid="live-contact-editor">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
            <User className="h-3.5 w-3.5" /> Contact principal (directeur)
          </div>
          {!editing && (
            <button onClick={() => { resetForm(); setEditing(true); }} data-testid="contact-edit-btn"
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-bold text-[var(--shariti-orange)] transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)]">
              <Pencil className="h-3 w-3" /> Modifier
            </button>
          )}
        </div>

        {!editing ? (
          <ContactChips contact={item.contact} />
        ) : (
          <div className="space-y-3" data-testid="contact-edit-form">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-[11px]">Civilité</Label>
                <Select value={form.civilite || "none"} onValueChange={(v) => set("civilite", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1 h-9" data-testid="contact-field-civilite"><SelectValue /></SelectTrigger>
                  <SelectContent>{CIVILITE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Fonction</Label>
                <Input value={form.fonction} onChange={(e) => set("fonction", e.target.value)}
                  placeholder="Directeur, Responsable…" className="mt-1 h-9" data-testid="contact-field-fonction" />
              </div>
              <div>
                <Label className="text-[11px]">Prénom</Label>
                <Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)}
                  className="mt-1 h-9" data-testid="contact-field-prenom" />
              </div>
              <div>
                <Label className="text-[11px]">Nom</Label>
                <Input value={form.nom} onChange={(e) => set("nom", e.target.value)}
                  className="mt-1 h-9" data-testid="contact-field-nom" />
              </div>
              <div>
                <Label className="text-[11px]">Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)}
                  placeholder="01 23 45 67 89" className="mt-1 h-9" data-testid="contact-field-telephone" />
              </div>
              <div>
                <Label className="text-[11px]">E-mail</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="contact@magasin.fr" className="mt-1 h-9" data-testid="contact-field-email" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => { setEditing(false); resetForm(); }} disabled={saving}
                data-testid="contact-cancel-btn" className="h-9 text-muted-ink hover:text-ink">
                <Undo2 className="mr-1.5 h-4 w-4" /> Annuler
              </Button>
              <Button onClick={save} disabled={saving} data-testid="contact-save-btn"
                className="h-9 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Enregistrer
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Contacts secondaires (lecture seule) */}
      {secondaires.filter((c) => contactName(c) || contactPhones(c).length).length > 0 && (
        <AllContacts spot={{ contact_principal: null, contacts_secondaires: secondaires }} />
      )}

      {/* Historique / traçabilité */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2">
        <div className="min-w-0 text-[11px] text-muted-ink" data-testid="contact-history-preview">
          {logsLoading ? (
            "Chargement de l'historique…"
          ) : lastContactLog ? (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3 shrink-0" />
              <span className="break-words">Dernière modif. : {activityTime(lastContactLog.timestamp)} · {lastContactLog.user_name || "—"}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Aucune modification de coordonnées</span>
          )}
        </div>
        <button onClick={() => setHistoryOpen(true)} data-testid="contact-history-open"
          className="inline-flex shrink-0 items-center gap-1 text-[11px] font-bold text-[var(--shariti-orange)] hover:underline">
          <History className="h-3 w-3" /> Voir l'historique{logs.length ? ` (${logs.length})` : ""}
        </button>
      </div>

      <ContactHistoryDrawer open={historyOpen} onOpenChange={setHistoryOpen}
        logs={logs} loading={logsLoading} spotName={item.normalized_name} />
    </div>
  );
}

// Pré-remplit les champs du script (point 5 « Verrouillage par e-mail », etc.)
// à partir des informations DÉJÀ enregistrées sur la fiche du magasin.
// Objectif : le CDP n'a plus qu'à VÉRIFIER/CONFIRMER l'e-mail, pas le re-saisir.
function spotPrefill(item) {
  const c = item.contact || {};
  const sec = (item.contacts_secondaires || [])[0] || {};
  const out = {};
  if (c.email) out.email = c.email;
  if (c.telephone) out.telephone = c.telephone;
  const secName = [sec.civilite, sec.prenom, sec.nom].filter(Boolean).join(" ").trim() || sec.nom || sec.prenom;
  if (secName) out.second_contact = secName;
  if (sec.email) out.second_contact_email = sec.email;
  const flux = item.flux?.global_value ?? (typeof item.flux === "number" ? item.flux : null);
  if (flux != null) out.flux = String(flux);
  return out;
}

function CallAssistant({ item, scripts, assocs, user, onNext, onSkip, onPrev, onClose, navigate, queuePosition, onStat, onContactUpdated, callbackMode, onBackToList }) {
  const qp = queuePosition || { index: -1, total: 0 };
  const hasPrev = qp.index > 0;
  const hasNext = qp.index >= 0 && qp.index + 1 < qp.total;
  const skip = onSkip || onNext;
  const prev = onPrev || (() => {});
  const [scriptKey, setScriptKey] = useState(item.script_key || "nouveau_partenaire");
  const [stepId, setStepId] = useState(null);
  const [stepHistory, setStepHistory] = useState([]);   // pile des étapes visitées (retour arrière)
  const [captured, setCaptured] = useState({});
  const [assocId, setAssocId] = useState(assocs[0]?.id || "");
  const [endResult, setEndResult] = useState(null);
  const [notes, setNotes] = useState("");
  const [callbackDate, setCallbackDate] = useState(item.next_callback_date || "");
  const [callbackTime, setCallbackTime] = useState(item.heure_rappel || "");
  const [confirmedDates, setConfirmedDates] = useState("");
  const [confirmedIso, setConfirmedIso] = useState([]);
  const [emailStep, setEmailStep] = useState(null); // null | "confirmation" | "proposition"
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setHistory(null); setHistLoading(true);
    api.get(`/prospection/spot-history/${item.spot_id}`)
      .then((r) => { if (alive) setHistory(r.data); })
      .catch(() => { if (alive) setHistory(null); })
      .finally(() => { if (alive) setHistLoading(false); });
    return () => { alive = false; };
  }, [item.spot_id]);

  const script = useMemo(() => scripts.find((s) => s.key === scriptKey) || scripts[0], [scripts, scriptKey]);
  const steps = script?.steps || [];
  const prefill = useMemo(() => spotPrefill(item), [item.spot_id, item.contact]); // eslint-disable-line
  useEffect(() => { setStepId(steps[0]?.id || null); setStepHistory([]); setCaptured(spotPrefill(item)); setEndResult(null); setNotes(""); setEmailStep(null); setConfirmedDates(""); setConfirmedIso([]); setCallbackDate(item.next_callback_date || ""); setCallbackTime(item.heure_rappel || ""); }, [scriptKey, item.spot_id]); // eslint-disable-line
  const step = steps.find((s) => s.id === stepId) || steps[0];
  const assoc = assocs.find((a) => a.id === assocId);

  const fill = (txt) => (txt || "")
    .replaceAll("{cdp}", user?.name || "")
    .replaceAll("{association}", assoc?.name || "l'association")
    .replaceAll("{enseigne}", item.normalized_name?.split(" ")[0] || "votre enseigne");

  const onBranch = (b) => {
    if (b.next?.startsWith("__end")) setEndResult(END_MAP[b.next] || "a_rappeler");
    else { setStepHistory((h) => [...h, stepId]); setStepId(b.next); }
  };

  // Retour arrière FLUIDE : depuis le panneau de fin -> revient au script ;
  // sinon dépile l'étape précédente. Ne casse jamais le fil (oubli d'une partie
  // du script, mauvaise branche cliquée…).
  const canGoBack = !!endResult || stepHistory.length > 0;
  const goBackStep = () => {
    if (endResult) { setEndResult(null); return; }
    setStepHistory((h) => {
      if (!h.length) return h;
      setStepId(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const result = endResult === "proposition" ? "oui_oral" : endResult;
      const finalNotes = [notes, confirmedDates ? `Dates convenues : ${confirmedDates}` : ""].filter(Boolean).join(" — ");
      await api.post("/prospection/call-result", {
        spot_id: item.spot_id, result, notes: finalNotes, captured,
        next_callback_date: callbackDate || undefined,
        heure_rappel: callbackTime || undefined, script_key: scriptKey,
      });
      toast.success("Appel enregistré");
      if (onStat) { onStat("traites"); if (endResult === "date_posee") onStat("accords"); }
      // Parcours fluide : bascule vers l'email pré-rempli sans quitter la page
      if (endResult === "date_posee") { setEmailStep("confirmation"); return; }
      if (endResult === "proposition") { setEmailStep("proposition"); return; }
      // Résultat non concluant : tracé + passage automatique au spot suivant
      onNext();
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Mode « rappel » : on garde le fil de la liste À rappeler + navigation à la chaîne */}
      {callbackMode && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.07)] px-3 py-2" data-testid="callback-mode-banner">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-[#B5470F]">
            <CalendarClock className="h-4 w-4" />
            Mode rappel
            {queuePosition && queuePosition.total > 0 && (
              <span className="rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold text-[#B5470F]">
                {queuePosition.index + 1} / {queuePosition.total}
              </span>
            )}
            {(item.next_callback_date || item.heure_rappel) && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-ink">
                <Clock className="h-3 w-3" />{frenchDate(item.next_callback_date)}{item.heure_rappel ? ` · ${item.heure_rappel}` : ""}
              </span>
            )}
          </div>
          <button onClick={onBackToList} data-testid="callback-back-to-list"
            className="inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.4)] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#B5470F] transition-colors hover:bg-[rgba(238,114,46,0.1)]">
            <ArrowLeft className="h-3.5 w-3.5" />Retour à la liste
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold leading-tight">{item.normalized_name}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-ink">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.ville} {item.dept ? `(${item.dept})` : ""}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StatusBadge status={item.status} />
            {queuePosition && queuePosition.total > 0 && queuePosition.index >= 0 && (
              <span className="text-[11px] font-semibold text-muted-ink" data-testid="queue-progress">
                {callbackMode ? "Rappel" : "Spot"} {queuePosition.index + 1} / {queuePosition.total}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <button onClick={prev} disabled={!hasPrev} data-testid="nav-prev" title="Précédent (flèche ←)"
                className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${hasPrev ? "border-[var(--border-soft)] text-ink hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]" : "cursor-not-allowed border-[var(--border-soft)] text-muted-ink opacity-40"}`}>
                <ChevronLeft className="h-3.5 w-3.5" />Précédent
              </button>
              <button onClick={skip} disabled={!hasNext} data-testid="nav-next" title={callbackMode ? "Rappel suivant (flèche →)" : "Spot suivant (flèche →)"}
                className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${hasNext ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.12)]" : "cursor-not-allowed border-[var(--border-soft)] text-muted-ink opacity-40"}`}>
                {callbackMode ? "Rappel suivant" : "Suivant"} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <span className="text-[9px] text-muted-ink" data-testid="nav-hint">Astuce : ← / → au clavier</span>
          </div>
        </div>
        {history?.next_booking && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border-2 border-[#E5484D] bg-[rgba(229,72,77,0.08)] px-3 py-2" data-testid="upcoming-booking-alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#C62828]" />
            <div className="text-[12px] leading-snug">
              <p className="font-bold text-[#B0201F]">Déjà programmé — à ne pas déranger</p>
              <p className="text-ink">
                Ce magasin est <b>réservé le {frenchDate(history.next_booking.date)}</b>
                {history.next_booking.status_label ? ` (${history.next_booking.status_label})` : ""}
                {history.next_booking.field_rebooking
                  ? ` — rebooking terrain${history.next_booking.booked_by ? ` par ${history.next_booking.booked_by}` : ""}`
                  : (history.next_booking.booked_by ? ` — booké par ${history.next_booking.booked_by}` : "")}.
                {history.upcoming && history.upcoming.length > 1 ? ` (+${history.upcoming.length - 1} autre date${history.upcoming.length - 1 > 1 ? "s" : ""})` : ""}
                {" "}Vérifiez l'historique ci-dessous avant d'appeler, pour ne pas télescoper une action déjà en cours.
              </p>
            </div>
          </div>
        )}
        <div className="mt-3">
          <LiveContactEditor item={item} user={user} onContactUpdated={onContactUpdated} />
        </div>
        <CallButton item={item} />
        {item.consignes && <div className="mt-3 rounded-xl border border-[rgba(252,219,83,0.6)] bg-[rgba(252,219,83,0.25)] px-3 py-2 text-xs font-medium text-[#5A4200]"><Info className="mr-1 inline h-3.5 w-3.5" />{item.consignes}</div>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={scriptKey} onValueChange={setScriptKey}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="script-select"><SelectValue /></SelectTrigger>
            <SelectContent>{scripts.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={assocId} onValueChange={setAssocId}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="assoc-select"><SelectValue placeholder="Association" /></SelectTrigger>
            <SelectContent>{assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <SpotHistoryCard history={history} loading={histLoading} />

      {emailStep ? (
        <ConfirmationFlow item={item} assocId={assocId} assocs={assocs} emailType={emailStep}
          initialDates={confirmedDates} initialIso={confirmedIso} user={user} navigate={navigate}
          onNext={onNext} onClose={onClose} onStat={onStat} />
      ) : (
      <>
      {!endResult ? (
        <AnimatePresence mode="wait">
          <motion.div key={stepId} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}
            className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="assistant-step">
            {stepHistory.length > 0 && (
              <button onClick={goBackStep} data-testid="step-back"
                className="mb-2 inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-semibold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]">
                <ArrowLeft className="h-3.5 w-3.5" /> Étape précédente
              </button>
            )}
            <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide" style={{ color: script?.color }}>
              <Sparkles className="h-3.5 w-3.5" /> {script?.name}
            </div>
            <h3 className="text-base font-extrabold">{step?.title}</h3>
            <p className="mt-2 whitespace-pre-line rounded-xl bg-[var(--surface-2)] p-4 text-sm leading-relaxed">{fill(step?.content)}</p>

            {(step?.fields || []).length > 0 && (
              <div className="mt-4 grid gap-3 @2xl/page:grid-cols-2">
                {step.fields.map((f) => {
                  const isPrefilled = !!prefill[f.key] && (captured[f.key] || "") === prefill[f.key];
                  return (
                  <div key={f.key}>
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-[11px]">{f.label}</Label>
                      {isPrefilled && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[9px] font-bold text-[#0F5A2F]" data-testid={`prefill-badge-${f.key}`}>
                          <CheckCircle2 className="h-2.5 w-2.5" /> depuis la fiche · à confirmer
                        </span>
                      )}
                    </div>
                    <Input type={f.type === "number" ? "number" : "text"} value={captured[f.key] || ""}
                      onChange={(e) => setCaptured((p) => ({ ...p, [f.key]: e.target.value }))}
                      className={`mt-1 h-9 ${isPrefilled ? "border-[rgba(31,157,85,0.45)] bg-[rgba(31,157,85,0.05)]" : ""}`} data-testid={`capture-${f.key}`} />
                  </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {(step?.branches || []).map((b, i) => {
                const isEnd = b.next?.startsWith("__end");
                return (
                  <Button key={i} onClick={() => onBranch(b)} data-testid={`branch-${i}`}
                    className={isEnd ? "bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" : "border border-[var(--border-soft)] bg-white text-ink hover:bg-[var(--surface-2)]"}>
                    {b.label} <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      ) : (
        <div className="space-y-2" data-testid="assistant-end-wrap">
          <button onClick={goBackStep} data-testid="end-back-to-script"
            className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-semibold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]">
            <ArrowLeft className="h-3.5 w-3.5" /> Revenir au script
          </button>
          <EndPanel endResult={endResult} setEndResult={setEndResult} captured={captured} notes={notes} setNotes={setNotes}
            callbackDate={callbackDate} setCallbackDate={setCallbackDate}
            callbackTime={callbackTime} setCallbackTime={setCallbackTime}
            confirmedDates={confirmedDates} setConfirmedDates={setConfirmedDates} setConfirmedIso={setConfirmedIso}
            spotId={item.spot_id} associationId={assocId}
            onSubmit={submit} saving={saving} />
        </div>
      )}

      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4">
        <div className="mb-2 text-xs font-bold text-muted-ink">Résultat rapide de l'appel</div>
        <div className="flex flex-wrap gap-2">
          {QUICK_RESULTS.map((q) => {
            const QI = q.icon;
            return (
              <button key={q.v} onClick={() => setEndResult(q.v)} data-testid={`quick-${q.v}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${endResult === q.v ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                <QI className="h-3.5 w-3.5" />{q.l}
              </button>
            );
          })}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function renderBold(text) {
  if (!text) return null;
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-bold text-ink">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}

const CONF_META = {
  faible: { label: "confiance faible", color: "#9B2226", bg: "rgba(214,69,69,0.12)" },
  moyenne: { label: "confiance moyenne", color: "#8A5A00", bg: "rgba(233,184,0,0.16)" },
  bonne: { label: "bonne confiance", color: "#0F5A2F", bg: "rgba(31,157,85,0.14)" },
};

function BestTimesBlock({ bt }) {
  if (!bt) return null;
  const conf = CONF_META[bt.confidence] || CONF_META.faible;
  const levelLabel = { spot: "ce spot", enseigne: `enseigne ${bt.enseigne || ""}`, global: "historique global" }[bt.level] || "historique";
  return (
    <div className="mt-3 rounded-xl border border-[rgba(238,114,46,0.25)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="best-times-block">
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 text-[12px] font-bold text-[var(--shariti-orange)]">
          <Clock className="h-3.5 w-3.5" />Meilleur créneau d'appel
        </div>
        <div className="flex items-center gap-1.5">
          <span className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-semibold text-muted-ink">basé sur : {levelLabel}</span>
          <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ background: conf.bg, color: conf.color }} data-testid="best-times-confidence">{conf.label}</span>
        </div>
      </div>

      {bt.sample_size === 0 ? (
        <p className="mt-1.5 text-[12px] text-muted-ink" data-testid="best-times-empty">
          Pas encore assez d'appels pour recommander un créneau — l'analyse s'affinera au fil des appels.
        </p>
      ) : (
        <>
          <p className="mt-1.5 text-[12px] leading-relaxed text-ink" data-testid="best-times-reco">{renderBold(bt.recommendation)}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-muted-ink">Meilleures plages</div>
              <div className="flex flex-wrap gap-1">
                {bt.best_slots.map((s) => (
                  <span key={s.key} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-1.5 py-0.5 text-[10px] font-bold" data-testid={`best-slot-${s.key}`}>
                    {s.label}<span className="text-[#0F5A2F]">{s.reach_rate}%</span>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-muted-ink">Meilleurs jours</div>
              <div className="flex flex-wrap gap-1">
                {bt.best_days.map((d) => (
                  <span key={d.dow} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-1.5 py-0.5 text-[10px] font-bold" data-testid={`best-day-${d.dow}`}>
                    {d.label}<span className="text-[#0F5A2F]">{d.reach_rate}%</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SpotHistoryCard({ history, loading }) {
  if (loading) {
    return <div className="h-24 animate-pulse rounded-2xl bg-[var(--surface-2)]" data-testid="spot-history-loading" />;
  }
  if (!history) return null;
  const perf = history.perf || {};
  const tier = perf.tier ? PERF_TIER[perf.tier] : null;
  const TIcon = tier?.icon;
  const visits = history.visits || [];
  const upcoming = history.upcoming || [];
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="spot-history-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold">
          <History className="h-4 w-4 text-[var(--shariti-orange)]" /> Historique du spot
        </div>
        {tier && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold"
            style={{ background: tier.bg, color: tier.color }} data-testid="spot-history-tier">
            <TIcon className="h-3 w-3" />{tier.label}
          </span>
        )}
      </div>

      {/* Résumé performance */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <div className="rounded-xl bg-[var(--surface-2)] p-2" data-testid="perf-avg">
          <div className="text-base font-extrabold text-ink">{perf.avg_per_day ?? 0}</div>
          <div className="text-[10px] text-muted-ink">dons / jour</div>
        </div>
        <div className="rounded-xl bg-[var(--surface-2)] p-2" data-testid="perf-valid">
          <div className="text-base font-extrabold text-ink">{perf.valid ?? 0}</div>
          <div className="text-[10px] text-muted-ink">dons cumulés</div>
        </div>
        <div className="rounded-xl bg-[var(--surface-2)] p-2" data-testid="perf-days">
          <div className="text-base font-extrabold text-ink">{perf.days ?? 0}</div>
          <div className="text-[10px] text-muted-ink">jours de présence</div>
        </div>
        <div className="rounded-xl bg-[var(--surface-2)] p-2" data-testid="perf-last">
          <div className="text-sm font-extrabold text-ink">{history.last_visit ? frenchDate(history.last_visit) : "—"}</div>
          <div className="text-[10px] text-muted-ink">dernière venue</div>
        </div>
      </div>

      {/* Meilleur créneau d'appel (analyse IA de l'historique des appels) */}
      <BestTimesBlock bt={history.best_times} />

      {/* DÉJÀ PROGRAMMÉ (bookings à venir / rebookings) — anti-télescopage */}
      {upcoming.length > 0 && (
        <div className="mt-3" data-testid="spot-upcoming">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold text-[#B0201F]">
            <CalendarCheck className="h-3.5 w-3.5" />Déjà programmé ({upcoming.length})
          </div>
          <ul className="space-y-1.5">
            {upcoming.map((u, i) => (
              <li key={`${u.date}-${i}`} className="flex items-center justify-between gap-2 rounded-xl border border-[rgba(229,72,77,0.35)] bg-[rgba(229,72,77,0.05)] px-3 py-1.5 text-xs" data-testid={`spot-upcoming-${i}`}>
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <CalendarClock className="h-3.5 w-3.5 text-[#C62828]" />{frenchDate(u.date)}
                  {u.association && <span className="rounded-full bg-[rgba(47,111,237,0.1)] px-1.5 py-0.5 text-[10px] font-bold text-[#1E4FB8]">{u.association}</span>}
                  {u.field_rebooking && <span className="rounded-full bg-[rgba(124,58,237,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#6D28D9]">Terrain</span>}
                </span>
                <span className="text-right text-muted-ink">{u.status_label}{u.booked_by ? ` · ${u.booked_by}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dernières venues */}
      <div className="mt-3">
        {visits.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--border-soft)] px-3 py-3 text-center text-xs text-muted-ink" data-testid="spot-history-empty">
            Première intervention sur ce spot — aucune venue enregistrée.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="spot-history-visits">
            {visits.map((v, i) => (
              <li key={`${v.date}-${i}`} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-1.5 text-xs" data-testid={`spot-visit-${i}`}>
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-ink" />{frenchDate(v.date)}
                  {v.association && <span className="rounded-full bg-[rgba(47,111,237,0.1)] px-1.5 py-0.5 text-[10px] font-bold text-[#1E4FB8]">{v.association}</span>}
                </span>
                <span className="inline-flex items-center gap-2 text-muted-ink">
                  {v.bulletins > 0 && <span className="font-bold text-[#0F5A2F]">{v.bulletins} don{v.bulletins > 1 ? "s" : ""}</span>}
                  {v.montant > 0 && <span className="inline-flex items-center gap-0.5"><Euro className="h-3 w-3" />{v.montant}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function EndPanel({ endResult, setEndResult, captured, notes, setNotes, callbackDate, setCallbackDate, callbackTime, setCallbackTime, confirmedDates, setConfirmedDates, setConfirmedIso, onSubmit, saving, spotId, associationId }) {
  const [isoLocal, setIsoLocal] = useState([]);
  const labels = {
    date_posee: "Booking confirmé", proposition: "Proposition de dates", a_rappeler: "À rappeler",
    refus_temporaire: "Refus temporaire", refus_definitif: "Refus définitif", nrp: "NRP", repondeur: "Répondeur", oui_oral: "Oui à l'oral",
  };
  const needCallback = ["a_rappeler", "refus_temporaire"].includes(endResult);
  const isPositive = ["date_posee", "proposition"].includes(endResult);
  const submitLabel = endResult === "date_posee" ? "Confirmer & préparer l'email"
    : endResult === "proposition" ? "Préparer la proposition"
    : "Enregistrer & spot suivant";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border-2 border-[var(--shariti-orange)] bg-white p-5 shadow-[var(--shadow-md)]" data-testid="end-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-extrabold">Fin d'appel — {labels[endResult]}</h3>
        <Button variant="ghost" size="sm" onClick={() => setEndResult(null)} className="text-xs">Reprendre le script</Button>
      </div>
      {Object.keys(captured).length > 0 && (
        <div className="mt-3 rounded-xl bg-[var(--surface-2)] p-3 text-xs">
          <div className="mb-1 font-bold text-muted-ink">Données capturées</div>
          <div className="flex flex-wrap gap-2">{Object.entries(captured).filter(([, v]) => v).map(([k, v]) => <span key={k} className="rounded-full bg-white px-2 py-0.5 font-semibold">{k}: {v}</span>)}</div>
        </div>
      )}
      {isPositive && (
        <div className="mt-3" data-testid="confirmed-dates">
          <DatesPicker value={confirmedDates} onChange={setConfirmedDates}
            onDatesChange={(iso) => { setConfirmedIso(iso); setIsoLocal(iso); }} testId="confirm-dates"
            label={endResult === "date_posee" ? "Date(s) convenue(s) avec le directeur" : "Date(s) proposée(s)"}
            helperText="Ces dates seront reprises automatiquement dans l'email." />
          <RotationDatesAlert spotId={spotId} associationId={associationId} isoDates={isoLocal} />
        </div>
      )}
      {needCallback && (
        <div className="mt-3" data-testid="callback-scheduler">
          <Label className="text-[11px]">Rappel programmé</Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Input type="date" value={callbackDate} onChange={(e) => setCallbackDate(e.target.value)}
              className="h-9 w-44" data-testid="callback-date" />
            <div className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2">
              <Clock className="h-3.5 w-3.5 text-muted-ink" />
              <Input type="time" value={callbackTime} onChange={(e) => setCallbackTime(e.target.value)}
                className="h-8 w-24 border-0 px-1 shadow-none focus-visible:ring-0" data-testid="callback-time" />
            </div>
            {(callbackDate || callbackTime) && (
              <button type="button" onClick={() => { setCallbackDate(""); setCallbackTime(""); }}
                className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink hover:text-ink"
                data-testid="callback-clear"><X className="h-3 w-3" /> Effacer</button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="callback-presets">
            {CALLBACK_PRESETS.map((p) => (
              <button key={p.key} type="button"
                onClick={() => { const r = p.compute(); setCallbackDate(r.d); setCallbackTime(r.t); }}
                className="rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-semibold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]"
                data-testid={`callback-preset-${p.key}`}>{p.label}</button>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-ink">
            L'heure alimente votre timeline « À rappeler ». Laissez vide si l'heure importe peu.
          </p>
        </div>
      )}
      <div className="mt-3"><Label className="text-[11px]">Notes de l'appel</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Compte-rendu, engagements du directeur…" data-testid="call-notes" /></div>
      <div className="mt-4 flex justify-end gap-2">
        {isPositive && <span className="mr-auto self-center text-xs text-muted-ink">L'email {endResult === "date_posee" ? "de confirmation" : "de proposition"} sera pré-rempli automatiquement.</span>}
        <Button onClick={onSubmit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="save-call-result">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{submitLabel} <ArrowRight className="ml-1 h-4 w-4" /></>}
        </Button>
      </div>
    </motion.div>
  );
}



function ConfirmationFlow({ item, assocId, assocs, emailType, initialDates, initialIso, user, navigate, onNext, onClose, onStat }) {
  const [dates, setDates] = useState(initialDates || "");
  const [isoDates, setIsoDates] = useState(initialIso || []);
  const [objet, setObjet] = useState("");
  const [corps, setCorps] = useState("");
  const [html, setHtml] = useState("");
  const [ack, setAck] = useState(null);
  const [preparing, setPreparing] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [extraCc, setExtraCc] = useState("");

  const assoc = assocs.find((a) => a.id === assocId);
  const c = item.contact || {};
  const to = c.email || "";
  const secondaryEmails = (item.contacts_secondaires || []).map((x) => x.email).filter(Boolean);
  const extraCcList = extraCc.split(/[,;\s]+/).map((s) => s.trim()).filter((e) => /.+@.+\..+/.test(e));
  const cc = Array.from(new Set([...secondaryEmails, ...extraCcList])).filter((e) => e && e !== to);
  const isConfirm = emailType === "confirmation";
  const title = isConfirm ? "Email de confirmation de booking" : "Email de proposition de dates";

  const buildFacts = (overrides = {}) => ({
    nom_directeur: [c.civilite, c.nom].filter(Boolean).join(" ") || "Madame, Monsieur",
    spot: item.normalized_name, association: assoc?.name, association_acronym: assoc?.acronym,
    association_id: assocId,
    adresse: item.address_full || item.ville || "",
    cdp: user?.name,
    dates, horaires: "10h à 20h", consignes: item.consignes || "",
    ...overrides,
  });

  const prepare = useCallback(async () => {
    setPreparing(true); setError(null);
    try {
      let overrides = {};
      let ackObj = ack;
      if (isConfirm) {
        const { data: ackData } = await api.post("/ack", {
          spot_id: item.spot_id, association_id: assocId, dates: dates || undefined,
          iso_dates: isoDates && isoDates.length ? isoDates : undefined,
          horaires: "10h à 20h", consignes: item.consignes || undefined,
        });
        ackObj = ackData; setAck(ackData);
        overrides.confirmation_url = `${window.location.origin}${ackData.path}`;
      }
      const { data } = await api.post("/emails/generate", { type: emailType, facts: buildFacts(overrides) });
      setObjet(data.objet); setCorps(data.corps); setHtml(data.html);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la préparation de l'email");
    } finally { setPreparing(false); }
  }, [item.spot_id, assocId, emailType, dates]); // eslint-disable-line

  // Génère automatiquement l'email dès l'entrée dans l'étape (parcours fluide)
  useEffect(() => { prepare(); /* eslint-disable-next-line */ }, []);

  // Met à jour l'aperçu HTML quand l'objet/corps sont édités (rendu serveur, debouncé)
  useEffect(() => {
    if (preparing || !objet) return;
    const t = setTimeout(async () => {
      try {
        const overrides = isConfirm && ack ? { confirmation_url: `${window.location.origin}${ack.path}` } : {};
        const { data } = await api.post("/emails/render", { type: emailType, objet, corps, facts: buildFacts(overrides) });
        setHtml(data.html);
      } catch { /* aperçu best-effort */ }
    }, 600);
    return () => clearTimeout(t);
  }, [objet, corps]); // eslint-disable-line

  const send = async () => {
    if (!to) { toast.error("Ce spot n'a pas d'email de contact"); return; }
    setSending(true);
    try {
      // On re-rend le HTML à partir du contenu éventuellement édité
      const overrides = isConfirm && ack ? { confirmation_url: `${window.location.origin}${ack.path}` } : {};
      const { data: rendered } = await api.post("/emails/render", { type: emailType, objet, corps, facts: buildFacts(overrides) });
      const res = await api.post("/emails/send", {
        spot_id: item.spot_id, association_id: assocId, type: emailType,
        to, cc, reply_to: user?.email, subject: objet, body_html: rendered.html, ack_id: ack?.id,
      });
      setSent(true);
      if (onStat) onStat("emails");
      toast.success(res.data?.attachment ? `Email envoyé à ${to} — mandat « ${res.data.attachment} » joint` : `Email envoyé à ${to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  // Pose EN 1 CLIC les dates confirmées directement dans le planning (pas de redirection)
  const bookToPlanning = async () => {
    if (!isoDates.length) { toast.error("Choisissez la/les date(s) sur le calendrier pour les poser au planning."); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/prospection/book-confirmed", {
        spot_id: item.spot_id, association_id: assocId, dates: isoDates, consignes: item.consignes || undefined,
      });
      setPosted(true);
      if (data.count > 0) {
        const forced = (data.created || []).filter((x) => x.forced).length;
        toast.success(`${data.count} date(s) posée(s) sur « ${data.ligne?.identifiant} »${forced ? ` — ${forced} en rebooking forcé` : ""}`);
      } else {
        toast.info("Ces dates étaient déjà planifiées.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible de poser au planning"); }
    finally { setPosting(false); }
  };

  // --- État : envoyé ---
  if (sent) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border-2 border-[#1F9D55] bg-white p-6 text-center shadow-[var(--shadow-md)]" data-testid="confirmation-sent">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(31,157,85,0.12)]">
          <MailCheck className="h-6 w-6 text-[#0F5A2F]" />
        </div>
        <h3 className="mt-3 text-base font-extrabold">Email envoyé à {to}</h3>
        <p className="mt-1 text-xs text-muted-ink">Traçabilité enregistrée. Vous pouvez enchaîner avec le spot suivant.</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button onClick={onNext} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="confirmation-next">
            Spot suivant <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          {isConfirm && (
            <Button variant="outline" onClick={bookToPlanning} disabled={posting || posted}
              className="border-[var(--border-soft)]" data-testid="confirmation-planning">
              {posting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : posted ? <CheckCircle2 className="mr-1 h-4 w-4 text-[#0F5A2F]" />
                : <CalendarClock className="mr-1 h-4 w-4" />}
              {posted ? "Posé au planning" : "Poser la date au planning"}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="text-xs" data-testid="confirmation-close">Terminer</Button>
        </div>
      </motion.div>
    );
  }

  // --- État : composition ---
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-[var(--shariti-orange)] bg-white p-5 shadow-[var(--shadow-md)]" data-testid="confirmation-flow">
      <div className="flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-extrabold">
          <MailCheck className="h-4 w-4 text-[var(--shariti-orange)]" /> {title}
        </h3>
        <button onClick={onNext} className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink hover:text-[var(--shariti-orange)]" data-testid="confirmation-skip">
          Passer sans envoyer <SkipForward className="h-3 w-3" />
        </button>
      </div>

      {/* Récapitulatif destinataire */}
      <div className="mt-3 rounded-xl bg-[var(--surface-2)] p-3 text-xs">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span><span className="text-muted-ink">Destinataire :</span> <b>{to || "aucun email"}</b></span>
          <span><span className="text-muted-ink">Association :</span> <b>{assoc?.name || "—"}</b></span>
        </div>
        <div className="mt-2">
          <Label className="inline-flex items-center gap-1 text-[11px] text-muted-ink">
            <Users className="h-3 w-3" /> En copie (facultatif) — 2ᵉ contact du magasin
          </Label>
          <Input value={extraCc} onChange={(e) => setExtraCc(e.target.value)}
            placeholder="ex : adjoint@magasin.fr (séparez par une virgule)"
            className="mt-1 h-8 text-xs" data-testid="confirmation-cc" />
          {cc.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid="confirmation-cc-chips">
              {cc.map((e) => (
                <span key={e} className="rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[10px] font-semibold text-[#B45309]">Cc : {e}</span>
              ))}
            </div>
          )}
        </div>
        {isConfirm && <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0F5A2F]"><Paperclip className="h-3 w-3" /> Le mandat actif sera joint automatiquement.</div>}
      </div>

      {!to && (
        <div className="mt-3 rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] px-3 py-2 text-xs font-medium text-[#8A1F1F]" data-testid="confirmation-no-email">
          Ce spot n'a pas d'email de contact — impossible d'envoyer. Complétez la fiche puis réessayez, ou passez au spot suivant.
        </div>
      )}

      <div className="mt-3">
        <DatesPicker value={dates} onChange={setDates} onDatesChange={setIsoDates} testId="confirmation-dates"
          label={isConfirm ? "Date(s) confirmée(s)" : "Date(s) proposée(s)"} />
        <RotationDatesAlert spotId={item.spot_id} associationId={assocId} isoDates={isoDates} />
      </div>

      {preparing ? (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-[var(--surface-2)] p-4 text-sm text-muted-ink" data-testid="confirmation-preparing">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--shariti-orange)]" /> Préparation de l'email par l'IA…
        </div>
      ) : error ? (
        <div className="mt-4 rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] p-4 text-sm text-[#8A1F1F]">
          {error}
          <div className="mt-2"><Button size="sm" variant="outline" onClick={prepare} className="border-[var(--border-soft)]" data-testid="confirmation-retry"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Réessayer</Button></div>
        </div>
      ) : (
        <>
          <div className="mt-3">
            <Label className="text-[11px]">Objet</Label>
            <Input value={objet} onChange={(e) => setObjet(e.target.value)} className="mt-1 h-9" data-testid="confirmation-subject" />
          </div>
          <div className="mt-3">
            <Label className="text-[11px]">Corps de l'email</Label>
            <Textarea value={corps} onChange={(e) => setCorps(e.target.value)} rows={8} className="mt-1 text-sm" data-testid="confirmation-body" />
          </div>
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              <Label className="inline-flex items-center gap-1 text-[11px]">
                <Paperclip className="h-3 w-3" /> Aperçu — mandat joint + bouton « Je confirme »
              </Label>
              <button type="button" onClick={() => setShowPreview((v) => !v)}
                className="text-[11px] font-semibold text-muted-ink hover:text-ink" data-testid="confirmation-preview-toggle">
                {showPreview ? "Masquer" : "Afficher"}
              </button>
            </div>
            {showPreview && (
              <iframe title="Aperçu de l'email" srcDoc={html} data-testid="confirmation-preview"
                className="h-[360px] w-full rounded-xl border border-[var(--border-soft)] bg-white" />
            )}
          </div>
        </>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={prepare} disabled={preparing} className="border-[var(--border-soft)]" data-testid="confirmation-regenerate">
          <Wand2 className="mr-1 h-4 w-4" /> Régénérer
        </Button>
        <Button onClick={send} disabled={sending || preparing || !to || !objet} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="confirmation-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-4 w-4" /> Envoyer l'email</>}
        </Button>
      </div>
    </motion.div>
  );
}


// Éditeur des NOTES DE REFUS enrichies (raison détaillée + stratégie de
// reconquête + date de relance suggérée). Chaque carte de refus peut ainsi
// documenter le contexte et préparer une reprise ciblée.
function RefusNotesEditor({ item, onSaved }) {
  const has = !!(item.refusal_detail || item.reconquest_strategy || item.reconquest_date);
  const [editing, setEditing] = useState(false);
  const [detail, setDetail] = useState(item.refusal_detail || "");
  const [strategy, setStrategy] = useState(item.reconquest_strategy || "");
  const [relance, setRelance] = useState(item.reconquest_date || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDetail(item.refusal_detail || "");
    setStrategy(item.reconquest_strategy || "");
    setRelance(item.reconquest_date || "");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/prospection/refus-notes", {
        spot_id: item.spot_id,
        refusal_detail: detail.trim() || null,
        reconquest_strategy: strategy.trim() || null,
        reconquest_date: relance || null,
      });
      toast.success("Notes de reconquête enregistrées");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="mt-3 space-y-2 rounded-xl border border-[rgba(238,114,46,0.3)] bg-[#FFF7F1] p-2.5" data-testid={`refus-notes-editor-${item.spot_id}`}>
        <div>
          <Label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#8A3A10]"><NotebookPen className="h-3 w-3" /> Raison détaillée du refus</Label>
          <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2}
            placeholder="Ex. : direction pas convaincue par le format 3 jours, mauvaise expérience passée…"
            className="mt-1 min-h-[52px] resize-none bg-white text-[13px]" data-testid={`refus-detail-${item.spot_id}`} />
        </div>
        <div>
          <Label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#8A3A10]"><Sparkles className="h-3 w-3" /> Stratégie de reconquête</Label>
          <Textarea value={strategy} onChange={(e) => setStrategy(e.target.value)} rows={2}
            placeholder="Ex. : reprise T2 avec offre 1 jour test, passer par le responsable rayon…"
            className="mt-1 min-h-[52px] resize-none bg-white text-[13px]" data-testid={`refus-strategy-${item.spot_id}`} />
        </div>
        <div>
          <Label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#8A3A10]"><CalendarClock className="h-3 w-3" /> Date de relance suggérée</Label>
          <Input type="date" value={relance} onChange={(e) => setRelance(e.target.value)}
            className="mt-1 h-9 bg-white text-[13px]" data-testid={`refus-relance-${item.spot_id}`} />
        </div>
        <div className="flex items-center gap-2 pt-0.5">
          <Button size="sm" variant="outline" className="h-8 flex-1" onClick={() => setEditing(false)} disabled={saving} data-testid={`refus-notes-cancel-${item.spot_id}`}>Annuler</Button>
          <Button size="sm" className="h-8 flex-1 gap-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" onClick={save} disabled={saving} data-testid={`refus-notes-save-${item.spot_id}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
          </Button>
        </div>
      </div>
    );
  }

  if (!has) {
    return (
      <button onClick={startEdit} data-testid={`refus-notes-add-${item.spot_id}`}
        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[rgba(238,114,46,0.4)] bg-[#FFF7F1] px-2 py-2 text-[12px] font-semibold text-[#8A3A10] transition-colors hover:bg-[#FFEFE3]">
        <NotebookPen className="h-3.5 w-3.5" /> Ajouter des notes de reconquête
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-1.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-1)] p-2.5" data-testid={`refus-notes-view-${item.spot_id}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink"><NotebookPen className="h-3 w-3" /> Reconquête</span>
        <button onClick={startEdit} data-testid={`refus-notes-edit-${item.spot_id}`}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]">
          <Pencil className="h-3 w-3" /> Modifier
        </button>
      </div>
      {item.refusal_detail && (
        <div className="text-[12px]"><span className="font-semibold text-ink">Raison : </span><span className="text-muted-ink">{item.refusal_detail}</span></div>
      )}
      {item.reconquest_strategy && (
        <div className="text-[12px]"><span className="font-semibold text-ink">Stratégie : </span><span className="text-muted-ink">{item.reconquest_strategy}</span></div>
      )}
      {item.reconquest_date && (
        <div className="flex items-center gap-1 text-[12px] font-semibold text-[#8A5200]"><CalendarClock className="h-3 w-3" /> Relance prévue le {frenchDate(item.reconquest_date)}</div>
      )}
    </div>
  );
}

// Liste DÉDIÉE des refus (définitifs + temporaires), hors file de prospection classique.
// Permet aux CDP une stratégie de reconquête à part, et de « reprendre » un spot.
function RefusView({ data, navigate, onRefresh }) {
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("all"); // all | temporaire | definitif

  const reactivate = async (spot) => {
    setBusyId(spot.spot_id);
    try {
      await api.post("/prospection/reactivate", { spot_id: spot.spot_id });
      toast.success(`« ${spot.normalized_name || "Spot"} » remis en prospection`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réactivation impossible");
    } finally { setBusyId(""); }
  };

  if (!data) {
    return <div className="grid place-items-center py-16 text-muted-ink"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  const items = (data.items || []).filter((it) => filter === "all" || it.refus_type === filter);
  const c = data.counts || {};

  return (
    <div className="space-y-4" data-testid="refus-view">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-3">
        <div className="mr-auto flex items-center gap-2 text-sm">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-[rgba(155,34,38,0.1)] text-[#9B2226]"><Ban className="h-5 w-5" /></span>
          <div>
            <div className="font-bold text-ink">Refus &amp; liste noire</div>
            <div className="text-[11px] text-muted-ink">Refus (temporaires/définitifs) et spots blacklistés depuis le planning — sortis de la file d'appels. Réactivez-en un d'un clic.</div>
          </div>
        </div>
        {[["all", `Tous (${c.total || 0})`], ["temporaire", `Refus temporaires (${c.temporaire || 0})`], ["definitif", `Liste noire (${c.definitif || 0})`]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`refus-filter-${v}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === v ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
            {l}
          </button>
        ))}
        <Button size="sm" variant="outline" className="h-8 gap-1.5 border-[var(--border-soft)] bg-white text-xs"
          onClick={() => downloadExport("/prospection/exports/refus", "refus_liste_noire.xlsx")}
          data-testid="export-refus-btn">
          <FileSpreadsheet className="h-3.5 w-3.5 text-[#9B2226]" /> Export Excel
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Ban} title="Aucun spot dans cette catégorie"
          description="Les refus (temporaires/définitifs) et les spots blacklistés depuis le planning apparaîtront ici, avec une réactivation en un clic." />
      ) : (
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => {
            const temporaire = it.refus_type === "temporaire";
            const phone = contactPhones(it.contact)[0];
            return (
              <div key={it.spot_id} className="flex flex-col rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]" data-testid={`refus-card-${it.spot_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="break-words font-bold text-ink">{it.normalized_name || "—"}</div>
                    <div className="flex items-start gap-1 break-words text-[12px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{[it.ville, it.dept].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${temporaire ? "bg-[rgba(230,148,52,0.16)] text-[#8A5200]" : "bg-[rgba(155,34,38,0.12)] text-[#9B2226]"}`}>
                    {temporaire ? it.refus_label : "Liste noire"}
                  </span>
                </div>

                <div className="mt-2 space-y-1 text-[12px]">
                  {temporaire && it.refus_end_date && (
                    <div className={`flex items-center gap-1 font-semibold ${it.expired ? "text-[#0F5A2F]" : "text-[#8A5200]"}`}>
                      <Clock className="h-3 w-3" />
                      {it.expired ? "Refus expiré — relançable" : `Disponible à partir du ${frenchDate(it.refus_end_date)}`}
                    </div>
                  )}
                  {it.refus_reason && <div className="line-clamp-2 italic text-muted-ink">« {it.refus_reason} »</div>}
                  {phone && <div className="flex items-center gap-1 text-muted-ink"><Phone className="h-3 w-3" />{phone}</div>}
                </div>

                <RefusNotesEditor item={it} onSaved={onRefresh} />

                <div className="mt-3 flex items-center gap-2 border-t border-[var(--border-soft)] pt-3">
                  <Button size="sm" variant="outline" className="h-8 flex-1 gap-1" onClick={() => navigate(`/spots/${it.spot_id}`)} data-testid={`refus-open-${it.spot_id}`}>
                    <ExternalLink className="h-3.5 w-3.5" /> Fiche
                  </Button>
                  <Button size="sm" className="h-8 flex-1 gap-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"
                    onClick={() => reactivate(it)} disabled={busyId === it.spot_id} data-testid={`refus-reactivate-${it.spot_id}`}>
                    {busyId === it.spot_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Réactiver
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function SessionRecap({ stats, onReset }) {
  const items = [
    { icon: PhoneCall, label: "Spots traités", value: stats.traites, color: "#2F6FED", bg: "rgba(47,111,237,0.1)" },
    { icon: CheckCircle2, label: "Accords obtenus", value: stats.accords, color: "#0F5A2F", bg: "rgba(31,157,85,0.12)" },
    { icon: MailCheck, label: "Emails envoyés", value: stats.emails, color: "#B45309", bg: "rgba(233,184,0,0.16)" },
  ];
  const taux = stats.traites ? Math.round((stats.accords / stats.traites) * 100) : 0;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[var(--border-soft)] bg-white p-8 text-center shadow-[var(--shadow-sm)]" data-testid="session-recap">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(233,184,0,0.18)]">
        <Trophy className="h-7 w-7 text-[#B45309]" />
      </div>
      <h3 className="mt-4 text-lg font-extrabold">Récap de votre session</h3>
      <p className="mt-1 text-sm text-muted-ink">Beau travail — vous avez traité toute votre file d'appels.</p>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {items.map((it) => {
          const I = it.icon;
          return (
            <div key={it.label} className="rounded-xl border border-[var(--border-soft)] p-4" data-testid={`recap-${it.label}`}>
              <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full" style={{ background: it.bg }}>
                <I className="h-4 w-4" style={{ color: it.color }} />
              </div>
              <div className="mt-2 text-2xl font-extrabold text-ink">{it.value}</div>
              <div className="text-[11px] text-muted-ink">{it.label}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs font-semibold">
        <TrendingUp className="h-3.5 w-3.5 text-[#0F5A2F]" /> Taux de transformation : {taux}%
      </div>
      <div className="mt-5">
        <Button onClick={onReset} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="session-reset">
          <RotateCcw className="mr-1 h-4 w-4" /> Actualiser la file
        </Button>
      </div>
    </motion.div>
  );
}


function GoogleSuggestions({ user, navigate, initialQ = "", autoDiscover = false }) {
  const [status, setStatus] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [view, setView] = useState("nouveau"); // "nouveau" (à prospecter) | "ajoute" (ajoutés)
  const [region, setRegion] = useState("IDF");
  const [dept, setDept] = useState("all");
  const [q, setQ] = useState(initialQ);
  const [sort, setSort] = useState("avis");
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [granularity, setGranularity] = useState("ville");
  const [maxRequests, setMaxRequests] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAdding, setBulkAdding] = useState(false);
  const [dupDialog, setDupDialog] = useState(null); // { candidate, matches } | null
  const [dupBusy, setDupBusy] = useState(false);
  const [ville, setVille] = useState("all");           // filtre ville du vivier
  const [scopeAll, setScopeAll] = useState(false);     // afficher aussi le hors-scope
  const [discoverCities, setDiscoverCities] = useState(() => new Set()); // villes ciblées (découverte)
  const isAdmin = user?.role === "admin";

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/prospection/google/status"); setStatus(data); return data; }
    catch { return null; }
  }, []);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { status: view, limit: 300, region, sort, scope: scopeAll ? "all" : "gd" };
      if (dept !== "all") params.dept = dept;
      if (ville !== "all") params.ville = ville;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/prospection/google/candidates", { params });
      setCandidates(data.candidates || []);
    } finally { setLoading(false); }
  }, [dept, ville, q, region, sort, view, scopeAll]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { const t = setTimeout(loadCandidates, 250); return () => clearTimeout(t); }, [loadCandidates]);

  // Arrivée depuis la Banque de spots (« Enrichir via Google ») : ouvre d'emblée
  // l'assistant de découverte pour un admin, prêt à cibler région/dept/ville.
  useEffect(() => {
    if (autoDiscover && isAdmin) setDiscoverOpen(true);
  }, [autoDiscover, isAdmin]);

  // Rafraîchit tant qu'un job de découverte tourne
  useEffect(() => {
    if (status?.job?.status !== "running") return;
    setDiscovering(true);
    const iv = setInterval(async () => {
      const d = await loadStatus();
      if (d?.job?.status !== "running") { clearInterval(iv); setDiscovering(false); loadCandidates(); }
    }, 3000);
    return () => clearInterval(iv);
  }, [status?.job?.status]); // eslint-disable-line

  const fetchEstimate = useCallback(async (gran) => {
    setEstimating(true);
    try {
      const body = { region, granularity: gran, dry_run: true };
      if (dept !== "all") body.depts = [dept];
      if (discoverCities.size > 0) body.cities = Array.from(discoverCities);
      const { data } = await api.post("/prospection/google/discover", body);
      setEstimate(data);
      setMaxRequests(String(data.estimated_requests || ""));
    } catch (e) {
      setEstimate(null);
    } finally { setEstimating(false); }
  }, [region, dept, discoverCities]);

  // Recalcule l'estimation à l'ouverture et à chaque changement de granularité/région/villes.
  useEffect(() => {
    if (discoverOpen) fetchEstimate(granularity);
  }, [discoverOpen, granularity, fetchEstimate]);

  const runDiscover = async () => {
    setDiscovering(true);
    try {
      const body = { region, granularity };
      if (dept !== "all") body.depts = [dept];
      if (discoverCities.size > 0) body.cities = Array.from(discoverCities);
      const mr = parseInt(maxRequests, 10);
      if (mr && mr > 0) body.max_requests = mr;
      await api.post("/prospection/google/discover", body);
      const label = regions[region]?.label || region;
      const gLabel = discoverCities.size > 0
        ? `sur ${discoverCities.size} ville(s) choisie(s)`
        : (granularity === "ville" ? "par ville (approfondie)" : "par département");
      toast.success("Découverte lancée", { description: `Recherche ${gLabel} — ${label}…` });
      setDiscoverOpen(false);
      setTimeout(loadStatus, 1500);
    } catch (e) {
      setDiscovering(false);
      toast.error(e?.response?.data?.detail || "Impossible de lancer la découverte");
    }
  };

  const addToBase = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/prospection/google/candidates/${c.id}/add`);
      if (data.already) {
        toast.info("Déjà en banque — doublon évité", {
          description: `${c.name} rattaché à ${data.id}${data.matched ? ` (via ${data.matched})` : ""}`,
        });
      } else {
        toast.success("Ajouté à la base", { description: `${c.name} — ${c.ville || ""}` });
      }
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      loadStatus();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === "object" && det.code === "ambiguous_duplicate") {
        setDupDialog({ candidate: c, matches: det.matches || [] });
      } else {
        toast.error((typeof det === "string" ? det : det?.message) || "Échec de l'ajout");
      }
    } finally { setBusyId(null); }
  };

  const linkToExisting = async (spotId) => {
    if (!dupDialog) return;
    setDupBusy(true);
    try {
      const c = dupDialog.candidate;
      const { data } = await api.post(`/prospection/google/candidates/${c.id}/link`, { spot_id: spotId });
      toast.success("Rattaché au spot existant — doublon évité", { description: `${c.name} → ${data.id}` });
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      setDupDialog(null);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rattachement impossible"); }
    finally { setDupBusy(false); }
  };

  const forceAdd = async () => {
    if (!dupDialog) return;
    setDupBusy(true);
    try {
      const c = dupDialog.candidate;
      await api.post(`/prospection/google/candidates/${c.id}/add?force=true`);
      toast.success("Ajouté à la base", { description: `${c.name} — ${c.ville || ""}` });
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      setDupDialog(null);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'ajout"); }
    finally { setDupBusy(false); }
  };

  const ignore = async (c) => {
    setBusyId(c.id);
    try {
      await api.post(`/prospection/google/candidates/${c.id}/ignore`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      loadStatus();
    } catch { toast.error("Échec"); }
    finally { setBusyId(null); }
  };

  // --- Sélection multiple (cases à cocher) ---
  // À prospecter : sélectionne les candidats à AJOUTER (pas déjà en base).
  // Ajoutés     : sélectionne les spots à RETIRER (tous, ils ont un spot lié).
  const isAddedView = view === "ajoute";
  const selectableIds = useMemo(
    () => candidates
      .filter((c) => (isAddedView ? !!c.added_spot_id : !c.in_base))
      .map((c) => c.id),
    [candidates, isAddedView]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  const bulkAddSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkAdding(true);
    try {
      const { data } = await api.post("/prospection/google/candidates/bulk-add", { ids });
      toast.success(`${data.added} spot(s) ajouté(s) à la base`, {
        description: [
          data.already ? `${data.already} déjà présent(s)` : null,
          data.flagged ? `${data.flagged} à vérifier (doublon proche)` : null,
          data.errors ? `${data.errors} erreur(s)` : null,
        ].filter(Boolean).join(" · ") || undefined,
      });
      const idset = new Set(ids);
      setCandidates((prev) => prev.filter((x) => !idset.has(x.id)));
      setSelected(new Set());
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'ajout multiple"); }
    finally { setBulkAdding(false); }
  };

  const bulkAddAll = async () => {
    setBulkAdding(true);
    try {
      const body = { all: true, region, status: "nouveau" };
      if (dept !== "all") body.dept = dept;
      if (q.trim()) body.q = q.trim();
      const { data } = await api.post("/prospection/google/candidates/bulk-add", body);
      toast.success(`${data.added} spot(s) ajouté(s) à la base`, {
        description: [
          `${data.requested} traité(s)`,
          data.already ? `${data.already} déjà présent(s)` : null,
          data.flagged ? `${data.flagged} à vérifier (doublon proche)` : null,
        ].filter(Boolean).join(" · "),
      });
      setSelected(new Set());
      loadCandidates();
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'ajout global"); }
    finally { setBulkAdding(false); }
  };

  // Retire de la base un spot ajouté par erreur depuis les suggestions Google.
  const removeFromBase = async (c) => {
    if (!c.added_spot_id) { toast.error("Spot lié introuvable"); return; }
    setBusyId(c.id);
    try {
      await api.delete(`/spots/${c.added_spot_id}`);
      toast.success("Retiré de la base", { description: `${c.display_name || c.name} — de nouveau proposé à la prospection.` });
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setBusyId(null); }
  };

  // Retrait EN MASSE des spots sélectionnés (vue « Ajoutés »).
  const bulkRemoveSelected = async () => {
    const selCands = candidates.filter((c) => selected.has(c.id) && c.added_spot_id);
    const spotIds = selCands.map((c) => c.added_spot_id);
    if (spotIds.length === 0) return;
    setBulkAdding(true);
    try {
      const { data } = await api.post("/spots/bulk-delete", { ids: spotIds });
      toast.success(`${data.deleted} spot(s) retiré(s) de la base`, {
        description: data.blocked ? `${data.blocked} bloqué(s) (déjà réservé/exploité)` : undefined,
      });
      const removedSpotIds = new Set(data.deleted_ids || []);
      setCandidates((prev) => prev.filter((x) => !removedSpotIds.has(x.added_spot_id)));
      setSelected(new Set());
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du retrait multiple"); }
    finally { setBulkAdding(false); }
  };

  const counts = status?.counts || {};
  const byDept = status?.by_dept || {};
  const depts = status?.depts || status?.idf_depts || {};
  const regions = status?.regions || {};
  const citiesByDept = status?.cities_by_dept || {};
  const cityOptions = dept !== "all" ? (citiesByDept[dept] || []) : [];
  const regionDepts = regions[region]?.depts || [];
  const regionLabel = regions[region]?.label || region;
  const regionCount = regionDepts.reduce((s, d) => s + (byDept[d] || 0), 0);
  const jobRunning = status?.job?.status === "running" || discovering;

  return (
    <div data-testid="google-suggestions">
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--border-soft)] bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"><Sparkles className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-bold leading-tight">Vivier de prospection</div>
            <div className="text-[11px] text-muted-ink" data-testid="google-counts">{regionCount} spot(s) à prospecter en {regionLabel} · {counts.nouveau || 0} au total</div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Bascule À prospecter / Ajoutés */}
          <div className="inline-flex rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-0.5" data-testid="google-view-toggle">
            <button onClick={() => setView("nouveau")} data-testid="google-view-nouveau"
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${!isAddedView ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink hover:text-ink"}`}>
              À prospecter
            </button>
            <button onClick={() => setView("ajoute")} data-testid="google-view-ajoute"
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${isAddedView ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink hover:text-ink"}`}>
              Ajoutés{counts.ajoute ? ` (${counts.ajoute})` : ""}
            </button>
          </div>
          {/* Région = périmètre : filtre l'affichage ET cible la découverte */}
          <Select value={region} onValueChange={(v) => { setRegion(v); setDept("all"); setVille("all"); setDiscoverCities(new Set()); }}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="google-region-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(regions).map(([code, r]) => (
                <SelectItem key={code} value={code}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dept} onValueChange={(v) => { setDept(v); setVille("all"); setDiscoverCities(new Set()); }}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="google-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les dépts. de la région</SelectItem>
              {regionDepts.map((code) => (
                <SelectItem key={code} value={code}>{code} · {depts[code] || code}{byDept[code] ? ` (${byDept[code]})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Sélecteur VILLE : affiné quand un département est choisi (granularité ville) */}
          {dept !== "all" && cityOptions.length > 0 && (
            <Select value={ville} onValueChange={setVille}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="google-ville-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les villes</SelectItem>
                {cityOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="google-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="avis">Trier : nb d'avis</SelectItem>
              <SelectItem value="note">Trier : note</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer (nom, ville)…" className="h-9 w-40 pl-8" data-testid="google-search" />
          </div>
          {!isAddedView && (
            <button
              onClick={() => setScopeAll((v) => !v)}
              title={scopeAll ? "Afficher uniquement la grande distribution" : "Afficher aussi le hors-scope (boulangeries, etc.)"}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${scopeAll ? "border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink" : "border-[rgba(238,114,46,0.4)] bg-[#FFF3E9] text-[#8A3A10]"}`}
              data-testid="google-scope-toggle">
              <Filter className="h-3.5 w-3.5" />
              {scopeAll ? "Tout" : "Grande distrib."}
            </button>
          )}
          {isAdmin && !isAddedView && (
            <Button onClick={() => setDiscoverOpen(true)} disabled={jobRunning} className="h-9 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="google-discover-btn">
              {jobRunning ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Recherche…</> : <><RefreshCw className="mr-1.5 h-4 w-4" />Découvrir ({regionLabel})</>}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent className="max-w-lg" data-testid="discover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[var(--shariti-orange)]" />
              Découvrir de nouveaux spots — {regionLabel}
            </DialogTitle>
            <p className="text-xs text-muted-ink">Google Places · les appels sont facturés, un plafond de sécurité s'applique.</p>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Profondeur de recherche</Label>
              <div className="mt-1.5 grid grid-cols-1 gap-2">
                <button type="button" onClick={() => setGranularity("dept")} data-testid="discover-gran-dept"
                  className={`rounded-xl border p-3 text-left transition-colors ${granularity === "dept" ? "border-[var(--shariti-orange)] bg-[#FFF7F1]" : "border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]"}`}>
                  <div className="text-sm font-bold">Standard — par département</div>
                  <div className="text-[11px] text-muted-ink">Rapide, économe. ~60 résultats max par enseigne/dépt.</div>
                </button>
                <button type="button" onClick={() => setGranularity("ville")} data-testid="discover-gran-ville"
                  className={`rounded-xl border p-3 text-left transition-colors ${granularity === "ville" ? "border-[var(--shariti-orange)] bg-[#FFF7F1]" : "border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]"}`}>
                  <div className="flex items-center gap-1.5 text-sm font-bold">Approfondie — par ville
                    <span className="rounded-full bg-[rgba(238,114,46,0.14)] px-2 py-0.5 text-[10px] font-bold text-[#8A3A10]">Gros volumes</span>
                  </div>
                  <div className="text-[11px] text-muted-ink">Interroge chaque ville : vivier démultiplié (~4 500 en IDF).</div>
                </button>
              </div>
            </div>

            {/* Choix de villes précises (quand un département est sélectionné) */}
            {dept !== "all" && cityOptions.length > 0 && (
              <div data-testid="discover-cities-block">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Villes ciblées — {depts[dept] || dept}</Label>
                  <button type="button" onClick={() => setDiscoverCities((prev) => prev.size === cityOptions.length ? new Set() : new Set(cityOptions))}
                    className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="discover-cities-toggle-all">
                    {discoverCities.size === cityOptions.length ? "Tout décocher" : "Tout cocher"}
                  </button>
                </div>
                <p className="mb-1.5 mt-0.5 text-[10px] text-muted-ink">Cochez une ou plusieurs villes pour enrichir précisément ces zones. Rien de coché = toutes les villes du département.</p>
                <div className="flex max-h-[140px] flex-wrap gap-1.5 overflow-y-auto no-scrollbar rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2" data-testid="discover-cities-list">
                  {cityOptions.map((c) => {
                    const on = discoverCities.has(c);
                    return (
                      <button key={c} type="button" data-testid={`discover-city-${c}`}
                        onClick={() => setDiscoverCities((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; })}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-ink hover:bg-[#FFF3E9]"}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
                {discoverCities.size > 0 && (
                  <div className="mt-1 text-[11px] font-semibold text-[#8A3A10]" data-testid="discover-cities-count">{discoverCities.size} ville(s) ciblée(s)</div>
                )}
              </div>
            )}

            <div className="flex items-start gap-1.5 rounded-lg bg-[rgba(47,111,237,0.07)] px-2.5 py-1.5 text-[11px] text-[#1F4E9B]" data-testid="discover-gd-note">
              <Filter className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Seuls les lieux de <span className="font-semibold">grande distribution</span> sont retenus (boulangeries, restaurants, stades… automatiquement écartés).
            </div>

            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="discover-estimate">
              {estimating ? (
                <div className="flex items-center gap-2 text-xs text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Estimation en cours…</div>
              ) : estimate ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-lg font-extrabold text-ink">{estimate.cities_total}</div><div className="text-[10px] text-muted-ink">villes ciblées</div></div>
                  <div><div className="text-lg font-extrabold text-ink">{estimate.estimated_requests}</div><div className="text-[10px] text-muted-ink">requêtes (plafond)</div></div>
                  <div><div className="text-lg font-extrabold text-[var(--shariti-orange)]">≈ {estimate.estimated_max_results?.toLocaleString("fr-FR")}</div><div className="text-[10px] text-muted-ink">résultats potentiels</div></div>
                </div>
              ) : (
                <div className="text-xs text-muted-ink">Estimation indisponible.</div>
              )}
              {estimate?.capped && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[rgba(233,184,0,0.14)] px-2 py-1.5 text-[11px] text-[#5A4200]">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  La couverture complète dépasse le plafond ({estimate.queries} requêtes possibles). Augmentez le plafond ci-dessous pour couvrir davantage.
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold">Plafond d'appels Google (sécurité budget)</Label>
              <Input type="number" min="1" value={maxRequests} onChange={(e) => setMaxRequests(e.target.value)}
                className="mt-1 h-9" data-testid="discover-max-requests" />
              <p className="mt-1 text-[10px] text-muted-ink">Chaque requête ramène jusqu'à 20 lieux. Les doublons (déjà en base ou déjà vus) sont automatiquement filtrés.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscoverOpen(false)} data-testid="discover-cancel">Annuler</Button>
            <Button onClick={runDiscover} disabled={discovering || estimating}
              className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="discover-launch">
              {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1.5 h-4 w-4" />Lancer la découverte</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {status && !status.configured && (
        <div className="mb-3 rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] px-3 py-2 text-xs font-semibold text-[#9B2226]">Clé Google Places non configurée côté serveur.</div>
      )}

      {isAddedView && (
        <div className="mb-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] text-muted-ink" data-testid="google-added-hint">
          <Info className="mr-1 inline h-3.5 w-3.5" />
          Spots intégrés à la base depuis Google. Vous pouvez en <span className="font-semibold text-ink">retirer</span> un tant qu'il n'a pas encore été réservé.
        </div>
      )}

      {loading ? (
        <div className="grid gap-2 @2xl/page:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-[var(--surface-2)]" />)}</div>
      ) : candidates.length === 0 ? (
        <EmptyState icon={Building2} title={isAddedView ? "Aucun spot ajouté" : "Aucune suggestion"}
          message={isAddedView
            ? "Les spots que vous ajoutez depuis les suggestions Google apparaîtront ici."
            : (jobRunning ? "Découverte en cours, patientez quelques instants…" : "Lancez une découverte pour alimenter le vivier de prospection.")} />
      ) : (
        <>
          {selectableIds.length > 0 && (
            <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-white/95 px-3 py-2 backdrop-blur" data-testid="bulk-select-toolbar">
              <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold" data-testid="bulk-select-all">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Tout sélectionner" />
                Tout sélectionner
              </label>
              <span className="text-xs text-muted-ink" data-testid="bulk-select-count">
                {someSelected ? `${selected.size} sélectionné${selected.size > 1 ? "s" : ""}` : `${selectableIds.length} ${isAddedView ? "spot(s)" : "à ajouter"}`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {someSelected && (
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}
                    className="h-8 px-2 text-muted-ink" data-testid="bulk-clear">
                    <X className="mr-1 h-3.5 w-3.5" />Effacer
                  </Button>
                )}
                {isAddedView ? (
                  <Button size="sm" onClick={bulkRemoveSelected} disabled={!someSelected || bulkAdding}
                    className="h-8 bg-[#C2410C] text-white hover:bg-[#9A3412]" data-testid="bulk-remove-selected">
                    {bulkAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1 h-3.5 w-3.5" />Retirer la sélection{someSelected ? ` (${selected.size})` : ""}</>}
                  </Button>
                ) : (
                  <>
                    <Button size="sm" onClick={bulkAddSelected} disabled={!someSelected || bulkAdding}
                      className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="bulk-add-selected">
                      {bulkAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Ajouter la sélection{someSelected ? ` (${selected.size})` : ""}</>}
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="outline" onClick={bulkAddAll} disabled={bulkAdding}
                        className="h-8 border-[var(--shariti-orange)] text-[var(--shariti-orange)] hover:bg-[#FFF3E9]" data-testid="bulk-add-all"
                        title="Ajouter tous les spots correspondant au filtre courant">
                        <ListChecks className="mr-1 h-3.5 w-3.5" />Tout ajouter (filtré)
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          <div className="grid gap-2 @2xl/page:grid-cols-2" data-testid="google-candidates-list">
          {candidates.map((c) => {
            const isSelectable = isAddedView ? !!c.added_spot_id : !c.in_base;
            const isSel = selected.has(c.id);
            return (
            <div key={c.id}
              className={`flex flex-col rounded-2xl border p-3 transition-colors ${isSel ? "border-[var(--shariti-orange)] bg-[#FFF9F5] ring-1 ring-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white"}`}
              data-testid={`google-candidate-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                  {isSelectable && (
                    <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(c.id)}
                      className="mt-0.5 shrink-0" aria-label={`Sélectionner ${c.display_name || c.name}`}
                      data-testid={`google-select-${c.id}`} />
                  )}
                  <div className="min-w-0">
                    <div className="break-words text-sm font-bold uppercase">{c.display_name || c.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.ville}{c.dept ? ` (${c.dept})` : ""}</span>
                      {typeof c.rating === "number" && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-current text-[#E8A400]" />{c.rating} · {c.user_ratings_total || 0} avis</span>}
                    </div>
                  </div>
                </div>
                {c.enseigne && <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.1)] px-2 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]">{c.enseigne}</span>}
              </div>
              {c.address_full && <div className="mt-1 line-clamp-1 text-[11px] text-muted-ink">{c.address_full}</div>}
              {c.telephone && (
                <a href={`tel:${c.telephone.replace(/[^0-9+]/g, "")}`}
                  className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[11px] font-semibold text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.22)]"
                  data-testid={`google-tel-${c.id}`}><Phone className="h-3 w-3" />{c.telephone}</a>
              )}

              {isAddedView ? (
                <>
                  <div className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(47,111,237,0.1)] px-2 py-0.5 text-[10px] font-semibold text-[#1E4FB8]" data-testid={`google-added-date-${c.id}`}>
                    <Sparkles className="h-3 w-3" /> Ajouté depuis Google le {frenchDate(c.updated_at)}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant="secondary"
                      onClick={() => navigate(`/spots/${c.added_spot_id}`)}
                      className="h-8 flex-1 border border-[var(--border-soft)] bg-white" data-testid={`google-open-${c.id}`}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />Ouvrir la fiche
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" disabled={busyId === c.id}
                          className="h-8 border-[rgba(214,69,69,0.4)] text-[#9B2226] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#9B2226]"
                          data-testid={`google-remove-${c.id}`} title="Retirer de la base">
                          {busyId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1 h-3.5 w-3.5" />Retirer</>}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="google-remove-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Retirer ce spot de la base ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            « {c.name} » sera supprimé de la banque de spots et de nouveau proposé à la prospection.
                            La suppression est refusée si le spot est déjà utilisé dans une réservation.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="google-remove-cancel">Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeFromBase(c)}
                            className="bg-[#C0392B] text-white hover:bg-[#A93226]" data-testid="google-remove-confirm">
                            Retirer de la base
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              ) : c.in_base ? (
                <>
                  <div className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-semibold text-[#0F5A2F]" data-testid={`google-inbase-${c.id}`}>
                    <CheckCircle2 className="h-3 w-3" /> Déjà dans la base
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant="secondary"
                      onClick={() => navigate(`/spots/${c.in_base_spot_id}`)}
                      disabled={!c.in_base_spot_id}
                      className="h-8 flex-1 border border-[var(--border-soft)] bg-white" data-testid={`google-opendup-${c.id}`}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />Ouvrir la fiche
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => ignore(c)} disabled={busyId === c.id}
                      className="h-8" data-testid={`google-ignore-${c.id}`} title="Ne plus proposer">
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" onClick={() => addToBase(c)} disabled={busyId === c.id}
                    className="h-8 flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid={`google-add-${c.id}`}>
                    {busyId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Ajouter à la base</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => ignore(c)} disabled={busyId === c.id}
                    className="h-8" data-testid={`google-ignore-${c.id}`} title="Ignorer">
                    <Ban className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            );
          })}
          </div>
        </>
      )}

      <AlertDialog open={!!dupDialog} onOpenChange={(o) => { if (!o) setDupDialog(null); }}>
        <AlertDialogContent data-testid="google-dup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-[var(--shariti-orange)]" /> Spot similaire déjà en banque
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Un ou plusieurs spots proches de la même enseigne existent déjà pour{" "}
                  <b>{dupDialog?.candidate?.name}</b>{dupDialog?.candidate?.ville ? ` — ${dupDialog.candidate.ville}` : ""}.
                  Pour éviter un doublon, <b>rattachez-le</b> au bon spot, ou ajoutez-le quand même s'il s'agit d'un magasin différent.
                </p>
                <div className="space-y-2">
                  {(dupDialog?.matches || []).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid={`google-dup-match-${m.id}`}>
                      <Building2 className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-[13px] font-semibold text-ink">{m.nom}</div>
                        <div className="flex items-center gap-2 text-[11px] text-muted-ink">
                          <span className="font-mono">{m.id}</span>
                          {m.ville ? <span>· {m.ville}</span> : null}
                          {m.distance_m != null ? <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{m.distance_m} m</span> : null}
                        </div>
                      </div>
                      <Button size="sm" onClick={() => linkToExisting(m.id)} disabled={dupBusy}
                        className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid={`google-dup-link-${m.id}`}>
                        {dupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="mr-1 h-3.5 w-3.5" />Lier</>}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDupDialog(null)} disabled={dupBusy}
              className="border-[var(--border-soft)] bg-white" data-testid="google-dup-cancel">
              Annuler
            </Button>
            <Button onClick={forceAdd} disabled={dupBusy} variant="outline"
              className="border-[var(--shariti-orange)] text-[var(--shariti-orange)] hover:bg-[#FFF3E9]" data-testid="google-dup-force">
              {dupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Ajouter quand même</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ==========================================================================
//  COUVERTURE DE ZONE (« force de frappe ») — mesure OBJECTIVE de l'épuisement
//  d'une zone : part des spots CONTACTÉS (≥ 1 appel) vs encore JAMAIS APPELÉS,
//  par département ou par ville. Permet de vérifier si les CDP exploitent bien
//  leurs secteurs avant de dire qu'ils sont « difficiles ».
// ==========================================================================
const COV_TIERS = [
  { max: 29, label: "À travailler", color: "#C0392B", bg: "rgba(192,57,43,0.10)", soft: "rgba(192,57,43,0.14)" },
  { max: 69, label: "En cours", color: "#B45309", bg: "rgba(238,114,46,0.12)", soft: "rgba(238,114,46,0.18)" },
  { max: 100, label: "Bien couverte", color: "#0F5A2F", bg: "rgba(31,157,85,0.12)", soft: "rgba(31,157,85,0.16)" },
];
const covTier = (pct) => COV_TIERS.find((t) => pct <= t.max) || COV_TIERS[COV_TIERS.length - 1];

function CoverageBar({ pct, color, testId }) {
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]" data-testid={testId}>
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color, transition: "width 240ms ease" }} />
    </div>
  );
}

function CoverageKpi({ label, value, hint, color, icon: Icon, testId }) {
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">
        {Icon ? <Icon className="h-3.5 w-3.5" style={{ color }} /> : null}{label}
      </div>
      <div className="mt-1.5 text-3xl font-extrabold leading-none text-ink" style={color ? { color } : undefined}>{value}</div>
      {hint ? <div className="mt-1 text-[11px] text-muted-ink">{hint}</div> : null}
    </div>
  );
}

function CoverageView({ navigate }) {
  const [meta, setMeta] = useState(null);          // régions + libellés dépts (via google/status)
  const [region, setRegion] = useState("IDF");
  const [dept, setDept] = useState("all");
  const [granularity, setGranularity] = useState("dept"); // "dept" | "ville"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qZone, setQZone] = useState("");          // recherche instantanée par ville / dépt
  const [expanded, setExpanded] = useState(() => new Set());  // zones dépliées (par clé)
  const [zoneSpots, setZoneSpots] = useState({});  // clé -> { loading, spots, counts }

  // Déplie/replie une zone et charge (à la demande) ses magasins détaillés.
  const toggleZone = useCallback((z, kid) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(kid)) { n.delete(kid); return n; }
      n.add(kid);
      if (!zoneSpots[kid]) {
        setZoneSpots((m) => ({ ...m, [kid]: { loading: true, spots: [], counts: {} } }));
        const params = {};
        if (z.dept) params.dept = z.dept;
        if (z.ville) params.ville = z.ville;
        if (!z.dept && !z.ville) params.region = region;
        api.get("/prospection/coverage/spots", { params })
          .then(({ data }) => setZoneSpots((m) => ({ ...m, [kid]: { loading: false, spots: data.spots || [], counts: data.counts || {} } })))
          .catch(() => setZoneSpots((m) => ({ ...m, [kid]: { loading: false, spots: [], counts: {}, error: true } })));
      }
      return n;
    });
  }, [zoneSpots, region]);

  useEffect(() => {
    api.get("/prospection/google/status").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setExpanded(new Set());   // on replie tout quand le périmètre change
    setZoneSpots({});
    try {
      const params = { region, granularity };
      if (dept !== "all") params.dept = dept;
      const { data } = await api.get("/prospection/coverage", { params });
      setData(data);
    } finally { setLoading(false); }
  }, [region, dept, granularity]);

  useEffect(() => { load(); }, [load]);

  const regions = meta?.regions || {};
  const deptNames = meta?.depts || {};
  const regionDepts = regions[region]?.depts || [];
  const regionLabel = regions[region]?.label || region;
  const totals = data?.totals || {};
  const zones = data?.zones || [];
  const restants = (totals.total || 0) - (totals.contactes || 0);
  const totalTier = covTier(totals.couverture_pct || 0);

  // Recherche instantanée : filtre la liste sur le nom de ville, de département ou son code.
  const nrm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const qn = nrm(qZone.trim());
  const visibleZones = qn
    ? zones.filter((z) => nrm(z.ville).includes(qn) || nrm(z.dept_nom).includes(qn) || nrm(z.dept).includes(qn))
    : zones;

  const zoneKey = (z) => (granularity === "ville" ? `${z.dept}-${z.ville || "?"}` : z.dept);
  const zoneTitle = (z) => {
    if (granularity === "ville") return z.ville || "Ville non renseignée";
    return z.dept_nom || z.dept || "Dépt. non renseigné";
  };
  const zoneSub = (z) => {
    if (granularity === "ville") return `${z.dept_nom || z.dept || "—"}${z.dept ? ` (${z.dept})` : ""}`;
    return `Département ${z.dept || "—"}`;
  };

  return (
    <div data-testid="coverage-view">
      {/* Barre d'intro + filtres de périmètre */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--border-soft)] bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"><Gauge className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-bold leading-tight">Force de frappe — couverture des zones</div>
            <div className="text-[11px] text-muted-ink">Part des spots déjà contactés vs encore jamais appelés, par zone.</div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Granularité : département ou ville */}
          <div className="inline-flex rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-0.5" data-testid="coverage-gran-toggle">
            <button onClick={() => setGranularity("dept")} data-testid="coverage-gran-dept"
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${granularity === "dept" ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink hover:text-ink"}`}>
              Par département
            </button>
            <button onClick={() => setGranularity("ville")} data-testid="coverage-gran-ville"
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${granularity === "ville" ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink hover:text-ink"}`}>
              Par ville
            </button>
          </div>
          <Select value={region} onValueChange={(v) => { setRegion(v); setDept("all"); }}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="coverage-region-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(regions).map(([code, r]) => (
                <SelectItem key={code} value={code}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="coverage-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les dépts. de la région</SelectItem>
              {regionDepts.map((code) => (
                <SelectItem key={code} value={code}>{code} · {deptNames[code] || code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
            <Input value={qZone} onChange={(e) => setQZone(e.target.value)}
              placeholder={granularity === "ville" ? "Rechercher une ville…" : "Rechercher un dépt…"}
              className="h-9 w-48 pl-8" data-testid="coverage-search" />
            {qZone && (
              <button type="button" onClick={() => setQZone("")} data-testid="coverage-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-ink hover:text-ink">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button variant="outline" onClick={load} disabled={loading}
            className="h-9 border-[var(--border-soft)] bg-white" data-testid="coverage-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Synthèse (KPIs) du périmètre sélectionné */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <CoverageKpi label="Spots du périmètre" value={loading ? "…" : (totals.total || 0)} icon={Building2}
          hint={`en ${dept === "all" ? regionLabel : (deptNames[dept] || dept)}`} testId="coverage-kpi-total" />
        <CoverageKpi label="Couverture" value={loading ? "…" : `${totals.couverture_pct || 0}%`} color={totalTier.color} icon={Gauge}
          hint={`${totals.contactes || 0} contacté(s) / ${totals.total || 0}`} testId="coverage-kpi-couverture" />
        <CoverageKpi label="Jamais appelés" value={loading ? "…" : restants} color="#C0392B" icon={PhoneOff}
          hint="spots encore à travailler" testId="coverage-kpi-restants" />
        <CoverageKpi label="Accords obtenus" value={loading ? "…" : (totals.positifs || 0)} color="#0F5A2F" icon={CheckCircle2}
          hint={`${totals.refus || 0} refus · ${totals.traite_pct || 0}% traités`} testId="coverage-kpi-positifs" />
      </div>

      {/* Détail par zone */}
      {loading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="coverage-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)]" />
          ))}
        </div>
      ) : zones.length === 0 ? (
        <EmptyState icon={Map} title="Aucune zone à afficher"
          message="Aucun spot n'est rattaché à ce périmètre. Enrichissez la base via les suggestions Google, puis revenez vérifier la couverture." />
      ) : visibleZones.length === 0 ? (
        <EmptyState icon={Search} title="Aucune zone trouvée"
          message={`Aucune ${granularity === "ville" ? "ville" : "zone"} ne correspond à « ${qZone.trim()} ».`}
          actionLabel="Effacer la recherche" onAction={() => setQZone("")} testId="coverage-search-empty" />
      ) : (
        <>
          <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-ink" data-testid="coverage-sort-hint">
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
            {qn
              ? `${visibleZones.length} zone(s) correspondent à « ${qZone.trim()} ».`
              : "Les zones les MOINS couvertes s'affichent en premier — ce sont les chantiers prioritaires."}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleZones.map((z, idx) => {
              const tier = covTier(z.couverture_pct || 0);
              const reste = (z.total || 0) - (z.contactes || 0);
              const kid = zoneKey(z);
              return (
                <div key={`${kid}-${idx}`} className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 transition-shadow hover:shadow-[var(--shadow-sm)]"
                  data-testid={`coverage-zone-${kid}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-ink">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
                        <span className="truncate">{zoneTitle(z)}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-ink">{zoneSub(z)}</div>
                    </div>
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: tier.color, backgroundColor: tier.soft }}>{tier.label}</span>
                  </div>

                  <div className="mt-3 flex items-end justify-between">
                    <div className="text-2xl font-extrabold leading-none" style={{ color: tier.color }} data-testid={`coverage-pct-${kid}`}>
                      {z.couverture_pct || 0}%
                    </div>
                    <div className="text-[11px] text-muted-ink">
                      {z.contactes || 0}/{z.total || 0} contactés
                    </div>
                  </div>
                  <div className="mt-1.5">
                    <CoverageBar pct={z.couverture_pct || 0} color={tier.color} testId={`coverage-bar-${kid}`} />
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
                    <div className="rounded-lg bg-[rgba(192,57,43,0.08)] py-1.5">
                      <div className="text-sm font-extrabold text-[#C0392B]">{reste}</div>
                      <div className="text-[9px] font-semibold uppercase text-muted-ink">Jamais appelés</div>
                    </div>
                    <div className="rounded-lg bg-[rgba(31,157,85,0.08)] py-1.5">
                      <div className="text-sm font-extrabold text-[#0F5A2F]">{z.positifs || 0}</div>
                      <div className="text-[9px] font-semibold uppercase text-muted-ink">Accords</div>
                    </div>
                    <div className="rounded-lg bg-[var(--surface-2)] py-1.5">
                      <div className="text-sm font-extrabold text-ink">{z.refus || 0}</div>
                      <div className="text-[9px] font-semibold uppercase text-muted-ink">Refus</div>
                    </div>
                  </div>

                  {z.last_contact ? (
                    <div className="mt-2.5 flex items-center gap-1 text-[10px] text-muted-ink">
                      <History className="h-3 w-3" />Dernier contact : {frenchDate(z.last_contact)}
                    </div>
                  ) : (
                    <div className="mt-2.5 flex items-center gap-1 text-[10px] font-semibold text-[#C0392B]">
                      <AlertTriangle className="h-3 w-3" />Zone jamais entamée
                    </div>
                  )}

                  {/* Bouton dépliant : liste des magasins de la zone */}
                  <button type="button" onClick={() => toggleZone(z, kid)}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] py-1.5 text-[11px] font-semibold text-ink transition-colors hover:bg-[#FFE9D8]"
                    data-testid={`coverage-expand-${kid}`}
                    aria-expanded={expanded.has(kid)}>
                    <Building2 className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                    {expanded.has(kid) ? "Masquer les magasins" : "Voir les magasins"}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.has(kid) ? "rotate-180" : ""}`} />
                  </button>

                  {expanded.has(kid) && (
                    <div className="mt-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2" data-testid={`coverage-spots-${kid}`}>
                      {zoneSpots[kid]?.loading ? (
                        <div className="space-y-1.5 py-1">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-8 animate-pulse rounded-lg bg-white/70" />
                          ))}
                        </div>
                      ) : (zoneSpots[kid]?.spots || []).length === 0 ? (
                        <div className="py-2 text-center text-[11px] text-muted-ink">Aucun magasin dans cette zone.</div>
                      ) : (
                        <div className="max-h-72 space-y-1 overflow-y-auto pr-0.5">
                          {(zoneSpots[kid].spots).map((sp) => (
                            <div key={sp.spot_id}
                              className="flex items-center gap-2 rounded-lg bg-white px-2 py-1.5"
                              data-testid={`coverage-spot-${sp.spot_id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-[11px] font-semibold text-ink" title={sp.normalized_name}>
                                  {sp.normalized_name || "—"}
                                </div>
                                <div className="flex items-center gap-2 text-[9.5px] text-muted-ink">
                                  {(sp.contact || {}).telephone ? (
                                    <span className="inline-flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{(sp.contact).telephone}</span>
                                  ) : null}
                                  {sp.last_contact ? (
                                    <span className="inline-flex items-center gap-0.5"><History className="h-2.5 w-2.5" />{frenchDate(sp.last_contact)}</span>
                                  ) : null}
                                </div>
                              </div>
                              <StatusBadge status={sp.status} testId={`coverage-spot-status-${sp.spot_id}`} />
                              {navigate ? (
                                <button type="button" onClick={() => navigate(`/spots/${sp.spot_id}`)}
                                  className="shrink-0 rounded-md p-1 text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)]"
                                  title="Ouvrir la fiche du spot" data-testid={`coverage-spot-open-${sp.spot_id}`}>
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
